/**
 * Sohbet penceresi bileşeni
 * Mesaj listesi, sohbet başlığı ve mesaj gönderme formunu içerir
 */

import { chatService, userService, auth, generateProfilePicture, presenceMetaFromLastSeenOnly, userCanRemoveOthersFromGroup, getMaxUploadSizeForProfile, isAdminOrControlProfile, isAdminProfile, describeGroupClose, FILE_SIZE_LIMIT_DEFAULT } from '../services/firebase.js';

export default class ChatWindow {
  constructor(headerContainer, messageListContainer, messageInputContainer) {
    this.headerContainer = headerContainer;
    this.messageListContainer = messageListContainer;
    this.messageInputContainer = messageInputContainer;
    
    this.currentChatId = null;
    this.currentUser = null;
    this.messages = [];
    this.typingTimeout = null;
    this.lastTypingStatus = false;
    this.unsubscribeMessages = null;
    this.unsubscribeTyping = null;
    this.unsubscribeHeaderPresence = null;
    this.typingUsers = {};
    this.userCache = {};
    this.canWrite = true; // Yazma yetkisi
    this.isAnnouncementChat = false; // Duyuru grubu mu?
    this.maxUploadSize = FILE_SIZE_LIMIT_DEFAULT;
    this.replyingTo = null; // Yanıtlanacak mesaj
    this.mentionList = null; // @ etiketleme listesi
    this.mentionUsers = []; // Etiketlenebilir kullanıcılar
    this.currentMention = null; // Şu anki @ arama
    
    // OPTIMIZASYON: Pagination için
    this.isLoadingOlderMessages = false;
    this.hasMoreMessages = true;
    this.oldestMessage = null;
    
    this.setPlaceholder();
    this.attachEventListeners();
    this.setupScrollListener();
    
    // Sürükle-bırak için event listener'ları ekle
    this.setupDragDropListeners();
  }
  
  async setUser(user) {
    this.currentUser = user;
    
    // Kullanıcı rolünü profil'den al ve currentUser'a ekle
    try {
      const userProfile = await userService.getUserProfile(user.uid);
      if (userProfile) {
        this.currentUser.userRole = userProfile.userRole || userProfile.role || '';
        this.currentUser.role = userProfile.userRole || userProfile.role || '';
      }
    } catch (error) {
      console.warn('Kullanıcı rolü alınamadı:', error);
    }
  }
  
  setPlaceholder() {
    this.messageListContainer.innerHTML = `
      <div class="empty-chat-placeholder">
        <div class="placeholder-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
        </div>
        <div class="placeholder-text">
          Sol taraftan bir sohbet seçin veya yeni bir sohbet başlatın.
        </div>
        <button class="placeholder-open-sidebar" onclick="document.querySelector('.sidebar').classList.add('open')">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
          Sohbetleri Aç
        </button>
      </div>
    `;
    
    // Mobil için header'da da hamburger menü göster
    this.headerContainer.innerHTML = `
      <button class="mobile-menu-button" title="Menü" style="margin-left: auto;">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>
    `;
    
    // Hamburger menü event listener
    const mobileMenuButton = this.headerContainer.querySelector('.mobile-menu-button');
    if (mobileMenuButton) {
      mobileMenuButton.addEventListener('click', () => {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
          sidebar.classList.toggle('open');
        }
      });
    }
    
    this.messageInputContainer.innerHTML = '';
    
    // CSS için ek stillemeler
    if (!document.querySelector('style#chat-window-styles')) {
      const style = document.createElement('style');
      style.id = 'chat-window-styles';
      style.textContent = `
        .empty-chat-placeholder {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--text-tertiary);
          padding: var(--space-xl);
          text-align: center;
        }
        
        .placeholder-icon {
          margin-bottom: var(--space-lg);
          opacity: 0.5;
        }
        
        .placeholder-text {
          font-size: var(--font-lg);
          max-width: 300px;
          line-height: 1.5;
          margin-bottom: var(--space-lg);
        }
        
        .placeholder-open-sidebar {
          display: none;
          padding: 12px 24px;
          background: var(--bg-accent);
          color: white;
          border: none;
          border-radius: var(--radius-md);
          font-size: var(--font-md);
          font-weight: 500;
          cursor: pointer;
          gap: 8px;
          align-items: center;
          transition: var(--transition-fast);
        }
        
        .placeholder-open-sidebar:hover {
          opacity: 0.9;
          transform: translateY(-2px);
        }
        
        .placeholder-open-sidebar svg {
          margin-right: 8px;
        }
        
        @media (max-width: 768px) {
          .placeholder-open-sidebar {
            display: inline-flex;
          }
          
          .placeholder-text {
            font-size: var(--font-md);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  attachEventListeners() {
    // Ana uygulama seviyesinde olayları dinle
    document.addEventListener('chat-selected', (e) => {
      const chatId = e.detail.chatId;
      this.loadChat(chatId);
    });
    
    // Sohbet arşivlendiğinde
    document.addEventListener('chat-archived', (e) => {
      const archivedChatId = e.detail.chatId;
      if (this.currentChatId === archivedChatId) {
        // Mevcut sohbet arşivlendi, temizle
        this.currentChatId = null;
        this.clearChat();
      }
    });
  }
  
  // OPTIMIZASYON: Scroll listener - yukarı scroll edildiğinde eski mesajları yükle
  setupScrollListener() {
    this.messageListContainer.addEventListener('scroll', () => {
      // Kullanıcı en üste yaklaştı mı?
      if (this.messageListContainer.scrollTop < 100 && 
          !this.isLoadingOlderMessages && 
          this.hasMoreMessages &&
          this.currentChatId) {
        this.loadOlderMessages();
      }
    });
  }
  
  // OPTIMIZASYON: Daha eski mesajları yükle
  async loadOlderMessages() {
    if (!this.oldestMessage || this.isLoadingOlderMessages || !this.hasMoreMessages) {
      return;
    }
    
    this.isLoadingOlderMessages = true;
    
    // Yükleniyor göstergesi ekle
    const loadingIndicator = document.createElement('div');
    loadingIndicator.className = 'loading-older-messages';
    loadingIndicator.innerHTML = `
      <div class="loading-spinner-small"></div>
      <span>Eski mesajlar yükleniyor...</span>
    `;
    this.messageListContainer.insertBefore(loadingIndicator, this.messageListContainer.firstChild);
    
    // Mevcut scroll pozisyonunu kaydet
    const scrollHeight = this.messageListContainer.scrollHeight;
    
    try {
      const olderMessages = await chatService.loadOlderMessages(
        this.currentChatId,
        this.oldestMessage,
        20
      );
      
      if (olderMessages.length > 0) {
        // Eski mesajları mevcut mesajların başına ekle
        this.messages = [...olderMessages, ...this.messages];
        this.oldestMessage = olderMessages[0];
        
        // Mesajları yeniden render et
        await this.renderMessages();
        
        // Scroll pozisyonunu koru (yeni mesajlar eklendiğinde scroll değişmesin)
        const newScrollHeight = this.messageListContainer.scrollHeight;
        this.messageListContainer.scrollTop = newScrollHeight - scrollHeight;
      } else {
        // Daha fazla mesaj yok
        this.hasMoreMessages = false;
      }
    } catch (error) {
      console.error('Eski mesajlar yüklenirken hata:', error);
    } finally {
      loadingIndicator.remove();
      this.isLoadingOlderMessages = false;
    }
    
    // Stil ekle
    if (!document.querySelector('style#loading-older-styles')) {
      const style = document.createElement('style');
      style.id = 'loading-older-styles';
      style.textContent = `
        .loading-older-messages {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-xs);
          padding: var(--space-sm);
          color: var(--text-secondary);
          font-size: var(--font-sm);
        }
        
        .loading-spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid var(--bg-tertiary);
          border-top-color: var(--bg-accent);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  // Yeni metod: Sürükle-bırak işlemleri için event listener'ları ekle
  setupDragDropListeners() {
    // Sürükle-bırak olaylarını dinleyecek element
    const dropTarget = this.messageListContainer;
    
    // Sürükleme olaylarını dinle
    dropTarget.addEventListener('dragover', (e) => this.handleDragOver(e));
    dropTarget.addEventListener('dragenter', (e) => this.handleDragEnter(e));
    dropTarget.addEventListener('dragleave', (e) => this.handleDragLeave(e));
    dropTarget.addEventListener('drop', (e) => this.handleDrop(e));
    
    // Stil eklemeleri
    if (!document.querySelector('style#drag-drop-styles')) {
      const style = document.createElement('style');
      style.id = 'drag-drop-styles';
      style.textContent = `
        .drag-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 123, 255, 0.15);
          border: 3px dashed rgba(0, 123, 255, 0.6);
          border-radius: var(--radius-md);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 100;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        
        .drag-overlay.active {
          opacity: 1;
        }
        
        .drag-icon {
          font-size: 48px;
          color: rgba(0, 123, 255, 0.8);
          margin-bottom: var(--space-md);
        }
        
        .drag-text {
          font-size: var(--font-lg);
          color: var(--text-accent);
          font-weight: 500;
          text-align: center;
        }
        
        .drag-subtext {
          font-size: var(--font-sm);
          color: var(--text-secondary);
          margin-top: var(--space-xs);
          text-align: center;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Sürükle-bırak overlay'ını oluştur
    const overlayDiv = document.createElement('div');
    overlayDiv.className = 'drag-overlay';
    overlayDiv.innerHTML = `
      <div class="drag-icon">📁</div>
      <div class="drag-text">Dosyanızı buraya bırakın</div>
      <div class="drag-subtext">Fotoğraf, video veya döküman eklemek için</div>
    `;
    
    // Overlay'ı DOM'a ekle (varsayılan olarak gizli)
    dropTarget.style.position = 'relative';
    dropTarget.appendChild(overlayDiv);
    this.dragOverlay = overlayDiv;
  }
  
  // Sürükleme olayı işleyicileri
  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
  
  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Overlay'ı görünür yap
    if (this.dragOverlay) {
      this.dragOverlay.classList.add('active');
    }
    
    return false;
  }
  
  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Mouse gerçekten alan dışına çıktıysa overlay'ı gizle
    const rect = this.messageListContainer.getBoundingClientRect();
    const isOut = e.clientX < rect.left || e.clientX > rect.right || 
                  e.clientY < rect.top || e.clientY > rect.bottom;
    
    if (isOut && this.dragOverlay) {
      this.dragOverlay.classList.remove('active');
    }
    
    return false;
  }
  
  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Overlay'ı gizle
    if (this.dragOverlay) {
      this.dragOverlay.classList.remove('active');
    }
    
    // Bırakılan dosyaları kontrol et
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0]; // Şimdilik sadece ilk dosyayı kullan
      
      // Mevcut uploadFile metodunu çağır
      this.uploadFile(file);
    }
    
    return false;
  }
  
  async loadChat(chatId) {
    if (!chatId || !this.currentUser) {
      //console.error("Sohbet yüklenemedi: Chat ID veya kullanıcı bilgisi eksik");
      return;
    }
    
    //console.log(`Sohbet yükleniyor: ${chatId}`);
    
    // Eğer daha önce bir sohbet dinleniyorsa, dinlemeyi durdur
    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
      this.unsubscribeMessages = null;
    }
    
    if (this.unsubscribeTyping) {
      this.unsubscribeTyping();
      this.unsubscribeTyping = null;
    }

    if (this.unsubscribeHeaderPresence) {
      this.unsubscribeHeaderPresence();
      this.unsubscribeHeaderPresence = null;
    }
    
    // Yeni sohbeti ayarla
    this.currentChatId = chatId;
    
    // OPTIMIZASYON: Pagination değişkenlerini sıfırla
    this.hasMoreMessages = true;
    this.oldestMessage = null;
    this.isLoadingOlderMessages = false;
    
    // Başlangıçta yükleniyor mesajı göster
    this.messageListContainer.innerHTML = `
      <div class="loading-messages">
        <div class="loading-spinner"></div>
        <div class="loading-text">Mesajlar yükleniyor...</div>
      </div>
    `;
    
    // Stil eklemeleri
    if (!document.querySelector('style#loading-styles')) {
      const style = document.createElement('style');
      style.id = 'loading-styles';
      style.textContent = `
        .loading-messages {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          text-align: center;
          color: var(--text-secondary);
        }
        
        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid var(--bg-tertiary);
          border-top-color: var(--bg-accent);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: var(--space-md);
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .loading-text {
          font-size: var(--font-sm);
        }
      `;
      document.head.appendChild(style);
    }
    
    // Sohbet detaylarını getir ve başlığı güncelle
    try {
      //console.log(`Sohbet detayları getiriliyor: ${chatId}`);
      const chatDetails = await chatService.getChatDetails(chatId);
      //console.log("Sohbet detayları alındı:", chatDetails);
      
      // Duyuru sohbeti ise ve kullanıcı admin/control değilse yazma yetkisi verme
      this.isAnnouncementChat = chatDetails.isAnnouncement || false;
      this.canWrite = true;

      const userProfile = await userService.getUserProfile(this.currentUser.uid);
      this.maxUploadSize = getMaxUploadSizeForProfile(userProfile);
      
      if (this.isAnnouncementChat) {
        console.log('Duyuru grubu yazma yetkisi kontrolü:', {
          isAnnouncementChat: this.isAnnouncementChat,
          userRole: userProfile?.userRole,
          canWrite: isAdminOrControlProfile(userProfile),
          userProfile: userProfile
        });
        
        this.canWrite = isAdminOrControlProfile(userProfile);
      }
      
      this.renderChatHeader(chatDetails);
      this.renderMessageInput(this.canWrite); // Bu fonksiyon içinde updateMentionUsers() çağrılıyor
      
      // Mesaj akışını dinle - OPTIMIZASYON: Pagination ile (son 50 mesaj)
      //console.log(`Mesaj akışı dinleniyor: ${chatId}`);
      
      // Arşivden çıkarılan sohbet için daha fazla mesaj yükle
      let messageLimit = 20; // Varsayılan limit
      if (chatDetails._loadArchivedMessages) {
        messageLimit = 100; // Arşivden çıkarılan sohbetler için daha fazla mesaj
      }
      
      // İlk yükleme flag'i
      let isFirstLoad = true;
      
      this.unsubscribeMessages = chatService.listenToChatMessages(chatId, async (messages) => {
        //console.log(`${messages.length} mesaj alındı`);
        
        // Mesajları işle
        this.messages = messages.map(msg => {
          // Hata ayıklama: null içerik kontrolü
          if (!msg.content && msg.type === 'text') {
            msg.content = '';
          }
          return msg;
        });
        
        // OPTIMIZASYON: En eski mesajı kaydet (pagination için)
        if (this.messages.length > 0) {
          this.oldestMessage = this.messages[0];
        }
        
        // Mesajları render et
        await this.renderMessages();
        
        // İlk yüklemede veya yeni mesaj geldiğinde en alta scroll
        if (isFirstLoad) {
          // İlk yükleme tamamlandı
          isFirstLoad = false;
          // Biraz bekle ki DOM güncellensin
          setTimeout(() => {
            this.scrollToBottom();
          }, 100);
        } else {
          // Kullanıcı en alttaysa scroll et
          const isAtBottom = this.messageListContainer.scrollHeight - this.messageListContainer.scrollTop <= this.messageListContainer.clientHeight + 100;
          if (isAtBottom) {
            this.scrollToBottom();
          }
        }
        
        // Okunmamış mesajları okundu olarak işaretle
        this.markMessagesAsRead();
      }, messageLimit);
      
      // Yazıyor durumunu dinle
      //console.log(`Yazıyor durumu dinleniyor: ${chatId}`);
      this.unsubscribeTyping = chatService.listenToTypingStatus(chatId, (users) => {
        this.typingUsers = users || {};
        this.updateTypingIndicator();
      });
      
    } catch (error) {
      //console.error("Sohbet yüklenirken hata:", error);
      this.messageListContainer.innerHTML = `
        <div class="error-messages">
          <div class="error-icon">⚠️</div>
          <div class="error-text">Sohbet yüklenirken bir hata oluştu. Lütfen sayfayı yenileyip tekrar deneyin.</div>
        </div>
      `;
      
      // Stil eklemeleri
      if (!document.querySelector('style#error-styles')) {
        const style = document.createElement('style');
        style.id = 'error-styles';
        style.textContent = `
          .error-messages {
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: var(--space-xl);
            text-align: center;
            color: var(--text-error);
          }
          
          .error-icon {
            font-size: 32px;
            margin-bottom: var(--space-md);
          }
          
          .error-text {
            font-size: var(--font-sm);
            max-width: 300px;
          }
        `;
        document.head.appendChild(style);
      }
    }
  }
  
  clearChat() {
    // Sohbet dinleyicilerini kapat
    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
      this.unsubscribeMessages = null;
    }
    
    if (this.unsubscribeTyping) {
      this.unsubscribeTyping();
      this.unsubscribeTyping = null;
    }

    if (this.unsubscribeHeaderPresence) {
      this.unsubscribeHeaderPresence();
      this.unsubscribeHeaderPresence = null;
    }
    
    // UI'ı temizle
    this.messageListContainer.innerHTML = `
      <div class="empty-chat">
        <div class="empty-icon">💬</div>
        <div class="empty-text">Sohbet seçin veya yeni bir sohbet başlatın</div>
      </div>
    `;
    
    this.headerContainer.innerHTML = '';
    this.messageInputContainer.innerHTML = '';
    
    // Stil eklemeleri
    if (!document.querySelector('style#empty-chat-styles')) {
      const style = document.createElement('style');
      style.id = 'empty-chat-styles';
      style.textContent = `
        .empty-chat {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          text-align: center;
          color: var(--text-secondary);
        }
        
        .empty-icon {
          font-size: 48px;
          margin-bottom: var(--space-md);
          opacity: 0.5;
        }
        
        .empty-text {
          font-size: var(--font-md);
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  async renderChatHeader(chatDetails) {
    if (!chatDetails) return;
    
    let headerContent = '';
    let canAddUser = false;
    let currentUserRole = '';
    if (chatDetails.isGroup) {
      // Kullanıcı rolünü kontrol et (admin veya control ise ekleme yetkisi ver)
      try {
        const userProfile = await userService.getUserProfile(this.currentUser.uid);
        currentUserRole = userProfile?.userRole?.toLowerCase() || 'user';
        canAddUser = ["admin", "control"].includes(currentUserRole);
      } catch (e) { canAddUser = false; }
      // Eğer bu grup bir announcement ise kullanıcı ekle butonu gösterme
      const isAnnouncement = chatDetails.isAnnouncement;
      headerContent = `
        <div class="chat-avatar">
          <div class="group-avatar">${chatDetails.name?.charAt(0) || 'G'}</div>
        </div>
        <div class="header-info">
          <div class="header-name">${chatDetails.name || 'Grup Sohbeti'}</div>
          <div class="header-status">${chatDetails.participants?.length || 0} üye</div>
        </div>
      `;
      // Header actions (butonlar)
      let headerActions = `
        <div class="header-actions">
          <button class="header-button" title="Ara">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>
          <button type="button" class="header-button" id="group-header-more-btn" title="Daha Fazla" aria-haspopup="dialog">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="19" cy="12" r="1"></circle>
              <circle cx="5" cy="12" r="1"></circle>
            </svg>
          </button>
          ${canAddUser && chatDetails.isGroup && !isAnnouncement ? `
          <button class="header-button add-user-header-btn" id="add-user-to-group-btn" title="Kullanıcı Ekle">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-8 0v2"/>
              <circle cx="12" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
          </button>
          ` : ''}
        </div>
        <button class="mobile-menu-button" title="Menü">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      `;
      this.headerContainer.innerHTML = headerContent + headerActions;
      // Kullanıcı Ekle butonu için event listener (sadece announcement olmayan gruplar)
      if (canAddUser && chatDetails.isGroup && !isAnnouncement) {
        const addUserBtn = this.headerContainer.querySelector('#add-user-to-group-btn');
        if (addUserBtn) {
          addUserBtn.addEventListener('click', () => this.showAddUserToGroupModal(chatDetails));
        }
      }
      const groupMoreBtn = this.headerContainer.querySelector('#group-header-more-btn')
      if (groupMoreBtn) {
        groupMoreBtn.addEventListener('click', () => this.showGroupMembersModal(chatDetails))
      }
    } else {
      // Özel sohbet
      const otherUserId = chatDetails.participants?.find(id => id !== this.currentUser?.uid);
      if (otherUserId) {
        let headerContent = '';
        try {
          // OPTIMIZASYON: skipCache kaldırıldı. İlk etikette önbellek kullanılır (0-1 okuma),
          // ardından aşağıdaki canlı abonelik başlığı güncel tutar. Böylece hem her açılıştaki
          // taze okuma maliyeti düşer hem de başlık artık gerçek zamanlı güncellenir (deneyim iyileşir).
          const otherUser = await userService.getUserProfile(otherUserId);
          if (otherUser) {
            const statusText = presenceMetaFromLastSeenOnly(otherUser).label
            // Kullanıcı adını ve rol bilgisini hazırla
            const displayName = otherUser.displayName || otherUser.name || otherUser.userCode || otherUser.id?.substring(0, 6) || 'Kullanıcı';
            const userRole = otherUser.userRole || otherUser.role || '';
            const roleDisplay = userRole ? `<span class="user-role-badge">${userRole}</span>` : '';
            const photoURL = otherUser.photoURL || 'https://www.bilcad.com/assets/vertical-no-tagline-transparent-250x250-BmXwcde2.svg';
            headerContent = `
              <div class="chat-avatar">
                <img src="${photoURL}" alt="${displayName}" onerror="this.onerror=null; this.src='https://www.bilcad.com/assets/vertical-no-tagline-transparent-250x250-BmXwcde2.svg';">
              </div>
              <div class="header-info">
                <div class="header-name-container">
                  <div class="header-name">${displayName}</div>
                  ${roleDisplay}
                </div>
                <div class="header-status">${statusText}</div>
              </div>
              <button class="mobile-menu-button" title="Menü">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
            `;
          } else {
            headerContent = `
              <div class="header-info"><div class="header-name">Bilinmeyen Kullanıcı</div></div>
              <button class="mobile-menu-button" title="Menü">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
            `;
          }
          this.headerContainer.innerHTML = headerContent;

          // OPTIMIZASYON/DENEYİM: Karşı tarafın son görülme durumunu canlı dinle ve başlığı
          // güncel tut. Firestore aynı users/{uid} belgesini paylaşılan önbellekten sunduğu
          // için ek okuma maliyeti minimumdur; skipCache tek seferlik okuması ise kaldırıldı.
          if (this.unsubscribeHeaderPresence) {
            this.unsubscribeHeaderPresence();
            this.unsubscribeHeaderPresence = null;
          }
          const headerChatId = this.currentChatId;
          this.unsubscribeHeaderPresence = chatService.subscribeChatUserLastSeen(otherUserId, (data) => {
            // Sohbet değiştiyse güncelleme yapma
            if (this.currentChatId !== headerChatId || !data) {
              return;
            }
            const statusEl = this.headerContainer.querySelector('.header-status');
            if (statusEl) {
              statusEl.textContent = presenceMetaFromLastSeenOnly(data).label;
            }
          });
        } catch (error) {
          //console.error("Kullanıcı bilgisi alınırken hata:", error);
          headerContent = `
            <div class="header-info"><div class="header-name">Kullanıcı bilgisi alınamadı</div></div>
            <button class="mobile-menu-button" title="Menü">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          `;
          this.headerContainer.innerHTML = headerContent;
        }
      }
    }
    
    // Stil eklemeleri
    if (!document.querySelector('style#header-styles')) {
      const style = document.createElement('style');
      style.id = 'header-styles';
      style.textContent = `
        .header-name-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .user-role-badge {
          font-size: var(--font-xs);
          background-color: var(--bg-tertiary);
          color: var(--text-secondary);
          padding: 2px 6px;
          border-radius: var(--radius-sm);
          font-weight: 500;
          white-space: nowrap;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Mobil menü butonu için event listener
    const mobileMenuButton = this.headerContainer.querySelector('.mobile-menu-button');
    if (mobileMenuButton) {
      mobileMenuButton.addEventListener('click', () => {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
          sidebar.classList.toggle('open');
        }
      });
    }
  }
  
  renderMessageInput(canWrite = true) {
    // Etiketlenebilir kullanıcıları güncelle
    this.updateMentionUsers();
    
    this.messageInputContainer.innerHTML = `
      <div class="input-container">
        <div class="input-actions">
          <button class="action-button" title="Emoji" ${!canWrite ? 'disabled style="opacity:0.5;cursor:not-allowed;"' : ''}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
              <line x1="9" y1="9" x2="9.01" y2="9"></line>
              <line x1="15" y1="9" x2="15.01" y2="9"></line>
            </svg>
          </button>
          <button class="action-button" id="attach-file-btn" title="Dosya Ekle" ${!canWrite ? 'disabled style="opacity:0.5;cursor:not-allowed;"' : ''}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
          </button>
          <input type="file" id="file-upload" accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" style="display: none;" ${!canWrite ? 'disabled' : ''}>
        </div>
        <textarea id="message-textarea" class="message-textarea" placeholder="${canWrite ? 'Mesajınızı yazın...' : 'Bu duyuru grubunda sadece yetkililer mesaj gönderebilir.'}" ${!canWrite ? 'disabled style="background:#f5f5f5;cursor:not-allowed;"' : ''}></textarea>
        <button id="send-message-btn" class="send-button" ${!canWrite ? 'disabled style="opacity:0.5;cursor:not-allowed;"' : ''}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
      <div id="typing-indicator" class="typing-indicator hidden"></div>
    `;
    
    // Event listenerlar
    const messageTextarea = document.getElementById('message-textarea');
    const sendButton = document.getElementById('send-message-btn');
    const attachButton = document.getElementById('attach-file-btn');
    const fileUpload = document.getElementById('file-upload');
    
    if (canWrite) {
      messageTextarea.addEventListener('keydown', (e) => {
        // Enter'a basıldığında mesaj gönder (shift+enter için yeni satır)
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.sendMessage();
        }
        
        // Yazıyor durumu
        this.handleTypingStatus();
      });
      
      // @ etiketleme için input listener
      messageTextarea.addEventListener('input', (e) => {
        this.handleMentionInput(e);
      });
      
      // Mention listesinde klavye navigasyonu
      messageTextarea.addEventListener('keydown', (e) => {
        if (this.mentionList && this.mentionList.style.display !== 'none') {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.navigateMentionList(e.key === 'ArrowDown' ? 1 : -1);
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            const selected = this.mentionList.querySelector('.mention-item.selected');
            if (selected) {
              e.preventDefault();
              selected.click();
            }
          } else if (e.key === 'Escape') {
            this.closeMentionList();
          }
        }
      });
      
      // Ctrl+V ile resim yapıştırma
      messageTextarea.addEventListener('paste', (e) => {
        const items = (e.clipboardData || e.originalEvent.clipboardData).items;
        
        for (const item of items) {
          if (item.type.indexOf('image') === 0) {
            const blob = item.getAsFile();
            //console.log('Yapıştırılan görsel:', blob);
            
            // Doğrudan yüklemeye başla
            this.uploadFile(blob);
            e.preventDefault();
            return;
          }
        }
      });
      
      sendButton.addEventListener('click', () => this.sendMessage());
      
      if (attachButton && fileUpload) {
        attachButton.addEventListener('click', () => {
          fileUpload.click();
        });
        
        fileUpload.addEventListener('change', (e) => {
          if (e.target.files && e.target.files[0]) {
            this.uploadFile(e.target.files[0]);
          }
        });
      }
    }
  }
  
  async renderMessages() {
    if (!this.messages.length) {
      this.messageListContainer.innerHTML = `
        <div class="empty-messages">
          <div class="empty-message">Henüz mesaj yok. Sohbete başlamak için mesaj gönderin.</div>
        </div>
      `;
      return;
    }
    
    // Mesajları grupla (aynı kişiden ardışık mesajlar ve tarih ayraçları)
    const messageGroups = this.groupMessages(this.messages);
    
    // Cache'i başlat
    if (!this.userCache) {
      this.userCache = {};
    }
    
    let html = '';
    
    messageGroups.forEach((group, index) => {
      // Eğer tarih ayracı ise
      if (group.type === 'date-separator') {
        html += `
          <div class="date-separator">
            <div class="date-line"></div>
            <div class="date-text">${group.displayText}</div>
            <div class="date-line"></div>
          </div>
        `;
        return;
      }
      
      // Normal mesaj grupları için
      const isCurrentUser = group.senderId === this.currentUser.uid;
      const messageClass = isCurrentUser ? 'sent' : 'received';
      
      // Kullanıcı bilgilerini al
      let senderName = isCurrentUser ? 'Ben' : 'Kullanıcı';
      let senderRole = '';
      
      // Cache'den kontrol et
      if (this.userCache[group.senderId]) {
        const user = this.userCache[group.senderId];
        
        // Cache'deki veri yapısını kontrol et
        if (user.displayName !== undefined && user.role !== undefined) {
          // Yeni format: {displayName, role}
          senderName = isCurrentUser ? 'Ben' : user.displayName;
          senderRole = user.role;
        } else {
          // Eski format: tam user objesi
          senderName = isCurrentUser ? 'Ben' : (user.userCode || user.code || user.displayName || user.name || group.senderId.substring(0, 6));
          senderRole = user.userRole || user.role || '';
        }
      } else {
        // Cache'de yoksa, yükleme placeholder'ı kullan
        senderName = isCurrentUser ? 'Ben' : 'Yükleniyor...';
        
        // Hemen async olarak kullanıcı bilgisini yükle
        this.loadUserInfoAsync(group.senderId);
      }
      
      // Rol gösterimi
      const roleDisplay = senderRole ? `<span class="sender-role">${senderRole}</span>` : '';
      
      html += `
        <div class="message-group ${messageClass}">
          <div class="message-sender">
            ${senderName} ${roleDisplay}
          </div>
      `;
      
      group.messages.forEach(message => {
        // Zaman formatla
        let timeDisplay = '';
        if (message.timestamp) {
          const date = message.timestamp.toDate ? message.timestamp.toDate() : new Date(message.timestamp);
          timeDisplay = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (message.createdAt) {
          const date = message.createdAt.toDate ? message.createdAt.toDate() : new Date(message.createdAt);
          timeDisplay = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        
        // Yanıtlanan mesajı göster
        let repliedMessageHtml = '';
        if (message.replyTo) {
          const repliedMsg = message.replyTo;
          let repliedUserName = 'Kullanıcı';
          
          // Cache'den kullanıcı adını al
          if (this.userCache[repliedMsg.senderId]) {
            repliedUserName = this.userCache[repliedMsg.senderId].displayName;
          } else {
            // Cache'de yoksa async olarak al ve placeholder kullan
            this.updateReplyUserName(repliedMsg.senderId, message.id);
          }
          
          let repliedContent = '';
          if (repliedMsg.type === 'image') {
            repliedContent = '📷 Resim';
          } else if (repliedMsg.type === 'video') {
            repliedContent = '🎥 Video';
          } else if (repliedMsg.type === 'file' || repliedMsg.type === 'document') {
            repliedContent = '📄 Dosya';
          } else {
            repliedContent = repliedMsg.content || '';
          }
          
          // Uzun içeriği kısalt
          if (repliedContent.length > 40) {
            repliedContent = repliedContent.substring(0, 37) + '...';
          }
          
          // Resim mesajları için özel önizleme
          let repliedPreview = repliedContent;
          if (repliedMsg.type === 'image' && repliedMsg.fileURL) {
            repliedPreview = `
              <div class="replied-image-preview">
                <img src="${repliedMsg.fileURL}" alt="Resim önizlemesi" class="replied-thumbnail">
                <span class="replied-image-text">📷 Resim</span>
              </div>
            `;
          }
          
          repliedMessageHtml = `
            <div class="replied-message" data-reply-to="${repliedMsg.messageId}" style="cursor: pointer;">
              <div class="replied-user">${repliedUserName}</div>
              <div class="replied-content">${repliedPreview}</div>
            </div>
          `;
        }
        
        // Mesaj içeriğini oluştur
        let messageContent = '';
        
        if (message.type === 'text') {
          // Text mesajı - URL'leri linkle
          messageContent = this.formatMessageText(message.content || '');
        } else if (message.type === 'image') {
          // Resim mesajı - lazy loading ve düşük kalite placeholder
          messageContent = `
            <img 
              src="${message.fileURL}" 
              alt="Resim" 
              class="message-image"
              loading="lazy"
              onerror="this.style.display='none'; this.parentElement.innerHTML='<div style=\\'padding:20px;text-align:center;color:var(--text-tertiary)\\'>Resim yüklenemedi</div>'"
            >
          `;
        } else if (message.type === 'video') {
          // Video mesajı
          messageContent = `
            <video controls class="message-video">
              <source src="${message.fileURL}" type="video/mp4">
              Tarayıcınız video etiketini desteklemiyor.
            </video>
          `;
        } else if (message.type === 'document' || message.type === 'file') {
          // Doküman veya dosya türü - geliştirilmiş görünüm
          const fileName = message.fileName || 'Dosya';
          const fileSize = message.fileSize ? this.formatFileSize(message.fileSize) : '';
          const fileType = message.fileType || '';
          const fileExtension = this.getFileExtension(fileName);
          const fileIcon = this.getFileIcon(fileExtension);
          
          messageContent = `
            <div class="file-attachment">
              <div class="file-icon">${fileIcon}</div>
              <div class="file-details">
                <div class="file-name">${fileName}</div>
                <div class="file-info">${fileExtension.toUpperCase()} ${fileSize ? `· ${fileSize}` : ''}</div>
              </div>
              <a href="${message.fileURL}" target="_blank" class="file-download" title="İndir">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </a>
            </div>
          `;
        } else {
          // Bilinmeyen mesaj tipi için
          messageContent = message.content || 'Mesaj';
        }
        
        // Okundu durumu (gönderen ben isem)
        let statusDisplay = '';
        if (isCurrentUser) {
          if (message.readBy && message.readBy.length > 1) {
            // Mesaj en az bir kişi tarafından okunmuş
            statusDisplay = `
              <span class="message-status" title="Okundu">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 11 12 14 22 4"></polyline>
                  <polyline points="21 12 21 19 3 19 3 5 12 5"></polyline>
                </svg>
              </span>
            `;
          } else if (message.timestamp) {
            // Mesaj gönderilmiş ve alınmış
            statusDisplay = `
              <span class="message-status" title="İletildi">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
            `;
          } else {
            // Mesaj henüz gönderiliyor
            statusDisplay = `
              <span class="message-status" title="Gönderiliyor">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline>
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                </svg>
              </span>
            `;
          }
        }
        
        // Düzenlendi ibaresi
        const editedLabel = message.edited ? '<span class="edited-label">(düzenlendi)</span>' : '';
        
        // Silindi mi kontrolü
        const isDeleted = message.deleted || false;
        let messageItemClass = isDeleted ? 'message-item deleted-message' : 'message-item';
        
        // Uzun mesajları tespit et (150+ karakter)
        const messageLength = message.content ? message.content.length : 0;
        if (messageLength > 150) {
          messageItemClass += ' long-message';
        }
        
        // Yanıt içeren mesajları tespit et
        if (message.replyTo) {
          messageItemClass += ' with-reply';
        }
        
        html += `
          <div class="${messageItemClass}" data-id="${message.id}" data-sender-id="${message.senderId}">
            ${repliedMessageHtml}
            ${messageContent}
            <div class="message-time">
              ${editedLabel}
              ${timeDisplay}
              ${statusDisplay}
            </div>
          </div>
        `;
      });
      
      html += `</div>`;
    });
    
    this.messageListContainer.innerHTML = html;
    
    // Mesaj sağ tıklama event listener'larını ekle
    this.attachMessageContextMenus();
    
    
    
    // CSS için ek stillemeler
    if (!document.querySelector('style#message-styles')) {
      const style = document.createElement('style');
      style.id = 'message-styles';
      style.textContent = `
        .empty-messages {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          text-align: center;
          color: var(--text-tertiary);
        }
        
        .message-group {
          margin-bottom: var(--space-md);
        }
        
        .message-sender {
          font-size: var(--font-sm);
          margin-bottom: var(--space-xs);
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: var(--space-xs);
        }
        
        .sender-role {
          font-size: var(--font-xs);
          background-color: var(--bg-tertiary);
          color: var(--text-secondary);
          padding: 2px 6px;
          border-radius: var(--radius-sm);
          font-weight: 500;
        }
        
        .message-item {
          max-width: 85%;
          min-width: 250px;
          word-wrap: break-word;
          overflow-wrap: break-word;
          white-space: normal;
        }
        
        .message-item br {
          line-height: 1.8;
        }
        
        .message-group.sent .message-item {
          max-width: 100%;
          min-width: 250px;
          margin-left: auto !important;
          margin-right: 0 !important;
        }
        
        .message-group.sent .message-item.long-message {
          max-width: 95%;
          min-width: 500px;
          width: fit-content;
          margin-left: auto !important;
          margin-right: 0 !important;
        }
        
        .message-group.received .message-item {
          max-width: 85%;
          min-width: 250px;
        }
        
        .message-item.long-message {
          max-width: 95%;
          min-width: 500px;
          width: fit-content;
        }
        
        .message-item.with-reply {
          max-width: 85%;
        }
        
        .message-image {
          max-width: 100%;
          width: 100%;
          height: auto;
          max-height: 400px;
          object-fit: cover;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          display: block;
          background: var(--bg-tertiary);
        }
        
        .message-image:hover {
          transform: scale(1.01);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }
        
        /* Resim yükleme durumu */
        .message-image[src=""] {
          min-height: 200px;
          background: linear-gradient(90deg, var(--bg-tertiary) 25%, var(--bg-hover) 50%, var(--bg-tertiary) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        .message-video {
          max-width: 100%;
          width: 100%;
          max-height: 400px;
          border-radius: var(--radius-md);
          display: block;
        }
        
        /* Yeni dosya gösterimi stilleri */
        .file-attachment {
          display: flex;
          align-items: center;
          background-color: rgba(0, 0, 0, 0.03);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: var(--radius-md);
          padding: var(--space-sm);
          position: relative;
          max-width: 100%;
          width: 280px;
        }
        
        .message-group.sent .file-attachment {
          background-color: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        .file-icon {
          margin-right: var(--space-md);
          flex-shrink: 0;
        }
        
        .file-details {
          flex: 1;
          min-width: 0;
        }
        
        .file-name {
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: 4px;
        }
        
        .file-info {
          font-size: var(--font-xs);
          color: var(--text-secondary);
        }
        
        .file-download {
          margin-left: var(--space-md);
          color: var(--text-accent);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        
        .file-download:hover {
          transform: translateY(-2px);
          opacity: 0.8;
        }
        
        .document-attachment {
          display: flex;
          align-items: center;
          background-color: rgba(0, 0, 0, 0.1);
          padding: var(--space-xs);
          border-radius: var(--radius-sm);
        }
        
        .document-info {
          margin-left: var(--space-sm);
        }
        
        .document-name {
          font-weight: 500;
          word-break: break-all;
          margin-bottom: 2px;
        }
        
        .document-download {
          font-size: var(--font-xs);
          color: var(--text-accent);
        }
        
        .typing-indicator {
          padding: var(--space-xs) var(--space-sm);
          color: var(--text-secondary);
          font-size: var(--font-xs);
          font-style: italic;
        }
        
        .message-url {
          color: var(--text-accent);
          text-decoration: underline;
          word-break: break-all;
        }

        .message-time {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: var(--font-xxs);
          color: var(--text-tertiary);
          white-space: nowrap;
          margin-top: 2px;
        }

        .message-status {
          display: flex;
          align-items: center;
        }
        
        .date-separator {
          display: flex;
          align-items: center;
          margin: var(--space-md) 0;
          color: var(--text-secondary);
          font-size: var(--font-sm);
        }
        
        .date-line {
          flex: 1;
          height: 1px;
          background-color: var(--bg-tertiary);
        }
        
        .date-text {
          padding: 0 var(--space-sm);
          font-weight: 500;
        }
        
        /* Mobil responsive */
        @media (max-width: 768px) {
          .message-item {
            max-width: min(350px, 80vw) !important;
          }
          
          .message-group.sent .message-item,
          .message-group.received .message-item {
            max-width: min(350px, 80vw) !important;
          }
          
          .message-image,
          .message-video {
            max-height: 300px;
          }
          
          .file-attachment {
            width: 100%;
            max-width: 100%;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Resimlere tıklandığında büyütme işlevi
    this.messageListContainer.querySelectorAll('.message-image').forEach(img => {
      img.addEventListener('click', () => {
        this.showImageFullscreen(img.src);
      });
    });
  }
  


  // Async kullanıcı bilgisi yükleme (render sırasında)
  async loadUserInfoAsync(senderId) {
    if (!this.userCache) {
      this.userCache = {};
    }
    
    if (!this.userCache[senderId]) {
      try {
        const user = await userService.getUserProfile(senderId);
        
        if (user) {
          // Farklı alan isimlerini kontrol et
          let displayName = user.displayName || user.name || user.userCode || user.code || user.id?.substring(0, 6) || 'Kullanıcı';
          const role = user.userRole || user.role || '';
          
          // Eğer displayName boş string ise, userCode'u kullan
          if (!displayName || displayName.trim() === '') {
            displayName = user.userCode || user.code || user.id?.substring(0, 6) || 'Kullanıcı';
          }
          
          this.userCache[senderId] = {
            displayName: displayName,
            role: role
          };
          
          // DOM'u güncelle
          this.updateSingleUserInDOM(senderId);
        }
      } catch (error) {
        console.warn(`Kullanıcı bilgisi alınamadı: ${senderId}`, error);
      }
    }
  }

  // Tek kullanıcıyı DOM'da güncelle
  updateSingleUserInDOM(senderId) {
    const messageGroups = this.messageListContainer.querySelectorAll('.message-group');
    
    messageGroups.forEach((group, index) => {
      const firstMessage = group.querySelector('.message-item[data-sender-id]');
      if (firstMessage) {
        const messageSenderId = firstMessage.getAttribute('data-sender-id');
        
        if (messageSenderId === senderId) {
          const senderElement = group.querySelector('.message-sender');
          
          if (senderElement && this.userCache[senderId]) {
            const isCurrentUser = senderId === this.currentUser.uid;
            
            // Cache'deki veri yapısını kontrol et
            let senderName, senderRole;
            
            if (this.userCache[senderId].displayName !== undefined && this.userCache[senderId].role !== undefined) {
              // Yeni format: {displayName, role}
              senderName = isCurrentUser ? 'Ben' : this.userCache[senderId].displayName;
              senderRole = this.userCache[senderId].role;
            } else {
              // Eski format: tam user objesi
              const user = this.userCache[senderId];
              senderName = isCurrentUser ? 'Ben' : (user.userCode || user.code || user.displayName || user.name || senderId.substring(0, 6));
              senderRole = user.userRole || user.role || '';
            }
            
            const roleDisplay = senderRole ? `<span class="sender-role">${senderRole}</span>` : '';
            senderElement.innerHTML = `${senderName} ${roleDisplay}`;
          }
        }
      }
    });
  }

  // Kullanıcı bilgilerini yükle ve mesajları güncelle
  async loadAndUpdateUserInfo() {
    if (!this.userCache) {
      this.userCache = {};
    }
    
    // Tüm gönderici kullanıcıları bul
    const senderIds = Array.from(new Set(this.messages.map(m => m.senderId)));
    
    // Kullanıcı bilgilerini toplu al
    const userPromises = senderIds.map(async (senderId) => {
      if (!this.userCache[senderId]) {
        try {
          const user = await userService.getUserProfile(senderId);
          if (user) {
            // Farklı alan isimlerini kontrol et
            const displayName = user.displayName || user.name || user.userCode || user.code || user.id?.substring(0, 6) || 'Kullanıcı';
            const role = user.userRole || user.role || user.userRole || '';
            
            this.userCache[senderId] = {
              displayName: displayName,
              role: role
            };
          }
        } catch (error) {
          console.warn(`Kullanıcı bilgisi alınamadı: ${senderId}`, error);
        }
      }
    });
    
    // Tüm kullanıcı bilgilerini paralel olarak yükle
    await Promise.all(userPromises);
    
    // Şimdi DOM'u güncelle
    this.updateMessageSendersInDOM();
  }

  // DOM'daki mesaj gönderenlerini güncelle
  updateMessageSendersInDOM() {
    const messageGroups = this.messageListContainer.querySelectorAll('.message-group');
    
    messageGroups.forEach((group, index) => {
      const senderElement = group.querySelector('.message-sender');
      
      if (senderElement) {
        // Mesaj grubunun ilk mesajından senderId'yi al
        const firstMessage = group.querySelector('.message-item[data-sender-id]');
        
        if (firstMessage) {
          const senderId = firstMessage.getAttribute('data-sender-id');
          
          if (senderId && this.userCache[senderId]) {
            const isCurrentUser = senderId === this.currentUser.uid;
            const senderName = isCurrentUser ? 'Ben' : this.userCache[senderId].displayName;
            const senderRole = this.userCache[senderId].role;
            const roleDisplay = senderRole ? `<span class="sender-role">${senderRole}</span>` : '';
            
            senderElement.innerHTML = `${senderName} ${roleDisplay}`;
          }
        }
      }
    });
  }

  // Yeni metod: Kullanıcı bilgilerini asenkron güncelleme
  async updateMessageSenders() {
    if (!this.userCache) {
      this.userCache = {};
    }
    
    // Tüm gönderici kullanıcıları bul
    const senderIds = Array.from(new Set(this.messages.map(m => m.senderId)));
    
    // Kullanıcı bilgilerini toplu al
    for (const senderId of senderIds) {
      if (!this.userCache[senderId]) {
        try {
          const user = await userService.getUserProfile(senderId);
          if (user) {
            // Farklı alan isimlerini kontrol et
            const displayName = user.displayName || user.name || user.userCode || user.code || user.id?.substring(0, 6) || 'Kullanıcı';
            const role = user.userRole || user.role || user.userRole || '';
            
            this.userCache[senderId] = {
              displayName: displayName,
              role: role
            };
          }
        } catch (error) {
          console.warn(`Kullanıcı bilgisi alınamadı: ${senderId}`, error);
        }
      }
    }
    
    // Şimdi her mesaj grubunu ayrı ayrı güncelle
    const messageGroups = this.messageListContainer.querySelectorAll('.message-group');
    messageGroups.forEach(group => {
      const senderElement = group.querySelector('.message-sender');
      if (senderElement) {
        // Mesaj grubunun ilk mesajından senderId'yi al
        const firstMessage = group.querySelector('.message-item[data-sender-id]');
        if (firstMessage) {
          const senderId = firstMessage.getAttribute('data-sender-id');
          if (senderId && this.userCache[senderId]) {
            const isCurrentUser = senderId === this.currentUser.uid;
            const senderName = isCurrentUser ? 'Ben' : this.userCache[senderId].displayName;
            const senderRole = this.userCache[senderId].role;
            const roleDisplay = senderRole ? `<span class="sender-role">${senderRole}</span>` : '';
            
            senderElement.innerHTML = `${senderName} ${roleDisplay}`;
          }
        }
      }
    });
  }
  
  // Yeni metod: Resimleri tam ekran gösterme
  showImageFullscreen(imageSrc) {
    const overlay = document.createElement('div');
    overlay.className = 'fullscreen-overlay';
    overlay.innerHTML = `
      <div class="fullscreen-container">
        <img src="${imageSrc}" class="fullscreen-image">
        <button class="close-fullscreen">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;
    
    document.body.appendChild(overlay);
    
    // Stil ekle
    if (!document.querySelector('style#fullscreen-image-styles')) {
      const style = document.createElement('style');
      style.id = 'fullscreen-image-styles';
      style.textContent = `
        .fullscreen-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        
        .fullscreen-container {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }
        
        .fullscreen-image {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
        }
        
        .close-fullscreen {
          position: absolute;
          top: -40px;
          right: 0;
          background: transparent;
          border: none;
          color: white;
          cursor: pointer;
          padding: 8px;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Kapatma işlemi
    const closeBtn = overlay.querySelector('.close-fullscreen');
    const closeImage = () => document.body.removeChild(overlay);
    
    closeBtn.addEventListener('click', closeImage);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeImage();
      }
    });
  }
  
  async uploadFile(file) {
    // Yazma yetkisi kontrolü
    if (!this.canWrite) {
      alert('Bu sohbete dosya gönderme yetkiniz yok.');
      return;
    }
    
    //console.log('Dosya yükleme başlatılıyor:', file.name, file.type, file.size);

    const modalHtml = `
      <div class="upload-modal">
        <div class="upload-modal-content">
          <div class="upload-header">
            <div class="file-info">
              <div class="file-icon">
                ${this.getFileIcon(this.getFileExtension(file.name))}
              </div>
              <div class="file-details">
                <div class="file-name">${file.name}</div>
                <div class="file-size">${this.formatFileSize(file.size)}</div>
              </div>
            </div>
            <button class="cancel-upload">×</button>
          </div>
          <div class="upload-progress-container">
            <div class="upload-progress" style="width: 0%"></div>
          </div>
          <div class="upload-status">Yüklemeye hazırlanıyor...</div>
        </div>
      </div>
    `;

    // Add modal styles if not already present
    if (!document.querySelector('#upload-modal-styles')) {
      const styles = document.createElement('style');
      styles.id = 'upload-modal-styles';
      styles.textContent = `
        .upload-modal {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: var(--bg-primary);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          width: 320px;
          z-index: 1000;
          overflow: hidden;
          animation: slideIn 0.3s ease-out;
          border: 1px solid var(--border-color);
        }
        
        @keyframes slideIn {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        .upload-modal-content {
          padding: 16px;
        }
        
        .upload-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .file-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .file-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-secondary);
          border-radius: 8px;
        }
        
        .file-icon svg {
          width: 24px;
          height: 24px;
          color: var(--text-secondary);
        }
        
        .file-details {
          flex: 1;
          min-width: 0;
        }
        
        .file-name {
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .file-size {
          font-size: 12px;
          color: var(--text-secondary);
        }
        
        .cancel-upload {
          background: none;
          border: none;
          color: var(--text-secondary);
          font-size: 24px;
          cursor: pointer;
          padding: 4px;
          line-height: 1;
        }
        
        .cancel-upload:hover {
          color: var(--text-primary);
        }
        
        .upload-progress-container {
          height: 4px;
          background: var(--bg-secondary);
          border-radius: 2px;
          overflow: hidden;
          margin-bottom: 12px;
        }
        
        .upload-progress {
          height: 100%;
          background: var(--bg-accent);
          transition: width 0.3s ease;
        }
        
        .upload-status {
          font-size: 13px;
          color: var(--text-secondary);
          text-align: center;
        }
      `;
      document.head.appendChild(styles);
    }

    // Create and add modal to document
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = modalHtml;
    const modal = modalContainer.firstElementChild;
    document.body.appendChild(modal);

    // Get modal elements
    const progressBar = modal.querySelector('.upload-progress');
    const statusText = modal.querySelector('.upload-status');
    const cancelButton = modal.querySelector('.cancel-upload');

    // Handle cancel button click
    let isCancelled = false;
    cancelButton.addEventListener('click', () => {
      isCancelled = true;
      modal.remove();
    });

    try {
      // Check file size
      const maxSize = this.maxUploadSize || FILE_SIZE_LIMIT_DEFAULT;
      if (file.size > maxSize) {
        throw new Error(`Dosya boyutu ${this.formatFileSize(maxSize)} limitini aşıyor (${this.formatFileSize(file.size)})`);
      }

      // Update status
      statusText.textContent = 'Yükleniyor...';

      // Upload file with progress tracking
      const result = await chatService.uploadAndSendFile(
        this.currentChatId,
        this.currentUser.uid,
        file,
        (progress) => {
          if (isCancelled) return;
          const percent = Math.round(progress * 100);
          progressBar.style.width = `${percent}%`;
          statusText.textContent = `Yükleniyor... %${percent}`;
        }
      );

      if (isCancelled) return;

      if (!result || !result.fileURL) {
        throw new Error('Dosya yüklenemedi: URL alınamadı');
      }

      // Show success message briefly before removing modal
      progressBar.style.background = 'var(--color-success)';
      statusText.textContent = 'Yükleme tamamlandı!';
      setTimeout(() => modal.remove(), 2000);

      //console.log('Dosya yükleme başarılı:', result);

    } catch (error) {
      if (isCancelled) return;

      //console.error('Dosya yükleme hatası:', error);
      progressBar.style.background = 'var(--color-error)';
      statusText.textContent = `Yükleme başarısız: ${error.message}`;
      
      setTimeout(() => modal.remove(), 3000);
    }
  }
  
  scrollToBottom() {
    this.messageListContainer.scrollTop = this.messageListContainer.scrollHeight;
  }
  
  handleTypingStatus() {
    // Yazıyor durumunun etkisini azaltmak için debouce uygula
    clearTimeout(this.typingTimeout);
    
    // Eğer yazıyor durumu true değilse, hemen güncelle
    if (!this.lastTypingStatus) {
      this.updateTypingStatus(true);
    }
    
    // Kullanıcı yazmayı bıraktıktan 1.5 saniye sonra durumu false yap
    this.typingTimeout = setTimeout(() => {
      this.updateTypingStatus(false);
    }, 1500);
  }
  
  async updateTypingStatus(isTyping) {
    if (!this.currentChatId || !this.currentUser) return;
    
    // Eğer durum değiştiyse, güncelle
    if (this.lastTypingStatus !== isTyping) {
      this.lastTypingStatus = isTyping;
      
      try {
        await chatService.updateTypingStatus(this.currentChatId, this.currentUser.uid, isTyping);
      } catch (error) {
        //console.error("Yazıyor durumu güncellenirken hata:", error);
      }
    }
  }
  
  updateTypingIndicator() {
    if (!this.currentUser) return;
    
    const typingIndicator = document.getElementById('typing-indicator');
    if (!typingIndicator) return;
    
    // Yazanları filtrele (kendim hariç)
    const typingUserIds = Object.keys(this.typingUsers).filter(id => id !== this.currentUser.uid);
    
    if (typingUserIds.length > 0) {
      typingIndicator.classList.remove('hidden');
      
      // Yazıyor mesajı oluştur
      Promise.all(typingUserIds.map(id => userService.getUserProfile(id)))
        .then(users => {
          const validUsers = users.filter(user => user !== null);
          
          let message = '';
          if (validUsers.length === 1) {
            message = `${validUsers[0].displayName} yazıyor...`;
          } else if (validUsers.length === 2) {
            message = `${validUsers[0].displayName} ve ${validUsers[1].displayName} yazıyor...`;
          } else if (validUsers.length > 2) {
            message = `${validUsers.length} kişi yazıyor...`;
          }
          
          typingIndicator.textContent = message;
        });
    } else {
      typingIndicator.classList.add('hidden');
    }
  }
  
  async markMessagesAsRead() {
    if (!this.currentChatId || !this.currentUser) return;
    
    //console.log("Okunmamış mesajlar işaretleniyor...");
    
    try {
      // Sadece bana ait olmayan mesajları işaretle
      const unreadsToMark = this.messages.filter(msg => 
        msg.senderId !== this.currentUser.uid && 
        (!msg.readBy || (Array.isArray(msg.readBy) && !msg.readBy.includes(this.currentUser.uid)))
      );
      
      //console.log(`${unreadsToMark.length} okunmamış mesaj bulundu`);
      
      if (unreadsToMark.length > 0) {
        // OPTIMIZASYON: Aynı mesaj kümesi için tekrar tekrar unreadCount=0 yazma.
        // readBy okuyucu için güncellenmediğinden unreadsToMark her snapshot'ta dolu gelir;
        // guard olmadan her render/typing snapshot'ında gereksiz yazım olur. Bu yazım chat
        // belgesini değiştirip TÜM katılımcıların sohbet listesi dinleyicisini okumaya zorlar.
        // Yalnızca en yeni okunmamış mesaj değiştiğinde (gerçek yeni mesaj) yaz.
        const newestUnreadId = unreadsToMark[unreadsToMark.length - 1].id;
        const markKey = `${this.currentChatId}:${newestUnreadId}`;
        if (this._lastMarkedReadKey === markKey) {
          return;
        }
        this._lastMarkedReadKey = markKey;

        // Tüm mesaj ID'lerini topla
        const messageIds = unreadsToMark.map(msg => msg.id);
        
        // Toplu güncelleme yap - bireysel güncellemeler yerine
        try {
          //console.log(`${messageIds.length} mesaj toplu olarak işaretleniyor...`);
          await chatService.markMessagesAsRead(this.currentChatId, messageIds, this.currentUser.uid);
          //console.log("Mesajlar başarıyla işaretlendi");
        } catch (error) {
          //console.error("Toplu mesaj işaretleme hatası:", error);
          
          // Toplu işaretleme başarısız olursa bireysel işaretleme dene
          //console.log("Bireysel işaretleme deneniyor...");
      for (const message of unreadsToMark) {
        try {
              //console.log(`Mesaj işaretleniyor: ${message.id}`);
          await chatService.markMessageAsRead(this.currentChatId, message.id, this.currentUser.uid);
            } catch (msgError) {
              //console.error(`Mesaj ${message.id} işaretlenemedi:`, msgError);
            }
          }
        }
      }
    } catch (error) {
      //console.error("Mesajları okundu işaretlerken genel hata:", error);
    }
  }
  
  groupMessages(messages) {
    const groups = [];
    let currentGroup = null;
    let currentDate = null;
    
    messages.forEach(message => {
      // Mesaj tarihi al
      const messageDate = message.timestamp 
        ? new Date(message.timestamp.toDate ? message.timestamp.toDate() : message.timestamp)
        : message.createdAt 
          ? new Date(message.createdAt.toDate ? message.createdAt.toDate() : message.createdAt)
          : new Date();
          
      // Yeni günü kontrol et
      const messageDay = messageDate.toLocaleDateString();
      
      // Gün değiştiyse bir tarih ayracı ekle
      if (messageDay !== currentDate) {
        currentDate = messageDay;
        
        // Özel tarih formatı - bugün, dün veya tarih
        let dateText = '';
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        const todayStr = today.toLocaleDateString();
        const yesterdayStr = yesterday.toLocaleDateString();
        
        if (messageDay === todayStr) {
          dateText = 'Bugün';
        } else if (messageDay === yesterdayStr) {
          dateText = 'Dün';
        } else {
          // Uzun tarih formatı (ör: 15 Nisan 2023)
          dateText = messageDate.toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
        }
        
        // Tarih ayracı için özel bir grup ekle
        groups.push({
          type: 'date-separator',
          date: messageDay,
          displayText: dateText
        });
        
        // Yeni bir kullanıcı grubu başlat
        currentGroup = null;
      }
      
      // Yeni grup oluştur veya mevcut gruba ekle
      if (!currentGroup || currentGroup.senderId !== message.senderId) {
        currentGroup = {
          type: 'messages',
          senderId: message.senderId,
          messages: [message]
        };
        groups.push(currentGroup);
      } else {
        currentGroup.messages.push(message);
      }
    });
    
    return groups;
  }
  
  formatMessageText(text) {
    if (!text) return '';
    
    // HTML karakterlerini escape et (XSS güvenliği)
    const escapeHtml = (str) => {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };
    
    // 1. URL'leri bul ve escape et (önce URL'leri işle)
    const urlPlaceholders = [];
    text = text.replace(/(https?:\/\/[^\s<]+)/g, (url) => {
      const placeholder = `___URL_${urlPlaceholders.length}___`
      urlPlaceholders.push(url)
      return placeholder
    })
    
    // 2. Proje etiketlerini bul ve placeholder ile değiştir
    const projectPlaceholders = [];
    
    // @"Proje Adı" formatı
    text = text.replace(/@\"([^\"]+)\"/g, (match, name) => {
      const placeholder = `___PROJECT_${projectPlaceholders.length}___`
      projectPlaceholders.push({ type: 'quoted-double', value: name })
      return placeholder
    })
    
    // @'Proje Adı' formatı
    text = text.replace(/@\'([^\']+)\'/g, (match, name) => {
      const placeholder = `___PROJECT_${projectPlaceholders.length}___`
      projectPlaceholders.push({ type: 'quoted-single', value: name })
      return placeholder
    })
    
    // @PROJE-KODU formatı (örn: @22063557-A)
    text = text.replace(/@([A-Za-z0-9]+-[A-Za-z0-9]+)/g, (match, code) => {
      const placeholder = `___PROJECT_${projectPlaceholders.length}___`
      projectPlaceholders.push({ type: 'code', value: code })
      return placeholder
    })
    
    // 3. HTML escape yap
    let formatted = escapeHtml(text);
    
    // 4. URL placeholder'ları gerçek linklerle değiştir
    urlPlaceholders.forEach((url, index) => {
      const link = `<a href="${url}" target="_blank" class="message-url">${url}</a>`
      formatted = formatted.replace(`___URL_${index}___`, link)
    })
    
    // 5. Proje placeholder'ları gerçek linklerle değiştir
    projectPlaceholders.forEach((project, index) => {
      const url = `https://www.bilcad.com/3bsayisallastirma.html?project=${encodeURIComponent(project.value)}`
      let link = ''
      if (project.type === 'quoted-double') {
        link = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="message-project">@"${escapeHtml(project.value)}"</a>`
      } else if (project.type === 'quoted-single') {
        link = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="message-project">@'${escapeHtml(project.value)}'</a>`
      } else {
        link = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="message-project">@${escapeHtml(project.value)}</a>`
      }
      formatted = formatted.replace(`___PROJECT_${index}___`, link)
    })
    
    // 6. Yeni satırları <br> etiketine çevir
    formatted = formatted.replace(/\n/g, '<br>');
    
    // 7. @ etiketlemelerini highlight et (proje etiketleri zaten işlendiği için kalan @'lar kullanıcı mention'ı)
    const mentionRegex = /@(\w+)/g;
    formatted = formatted.replace(mentionRegex, '<span class="message-mention">@$1</span>');
    
    return formatted;
  }
  
  async sendMessage(content = null, type = 'text', additionalData = {}) {
    try {
      // Yazma yetkisi kontrolü
      if (!this.canWrite) {
        alert('Bu sohbete mesaj gönderme yetkiniz yok.');
        return;
      }
      
      // İçerik parametresi verilmediyse textarea'dan al
      if (content === null) {
        const messageTextarea = document.getElementById('message-textarea');
        content = messageTextarea.value.trim();
        
        // Boş mesaj kontrolü
        if (!content && type === 'text') {
          return;
        }
        
        // Text mesajı ise textarea'yı temizle
        if (type === 'text') {
          messageTextarea.value = '';
        }
      }
      
      if (!this.currentChatId || !this.currentUser) {
        //console.error('Mesaj gönderilemedi: Sohbet veya kullanıcı bilgisi eksik');
        return;
      }
      
      // Yazıyor durumunu güncelle
      this.updateTypingStatus(false);
      
      // Yanıt bilgisini ekle
      if (this.replyingTo) {
        additionalData.replyTo = {
          messageId: this.replyingTo.id,
          senderId: this.replyingTo.senderId,
          content: this.replyingTo.content,
          type: this.replyingTo.type,
          fileURL: this.replyingTo.fileURL
        };
      }
      
      // Mesajı gönder
      await chatService.sendMessage(
        this.currentChatId,
        this.currentUser.uid,
        content,
        type,
        additionalData
      );
      
      // Yanıtı temizle
      this.cancelReply();
      
      // UI'ı güncelle
      this.scrollToBottom();
    } catch (error) {
      //console.error('Mesaj gönderme hatası:', error);
      alert('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    }
  }

  ensureGroupMembersModalStyles () {
    if (document.querySelector('style#group-members-modal-styles')) {
      return
    }
    const st = document.createElement('style')
    st.id = 'group-members-modal-styles'
    st.textContent = `
      .group-members-modal-content {
        max-width: 420px;
        width: calc(100% - 32px);
        max-height: min(80vh, 640px);
        display: flex;
        flex-direction: column;
      }
      .group-members-subtitle {
        margin: 0 var(--space-lg);
        font-size: var(--font-sm);
        color: var(--text-secondary);
      }
      .group-members-modal-body {
        flex: 1;
        min-height: 120px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .group-members-loading {
        padding: var(--space-xl);
        text-align: center;
        color: var(--text-secondary);
        font-size: var(--font-sm);
      }
      .group-members-loading.hidden,
      .group-members-list.hidden {
        display: none !important;
      }
      .group-members-list {
        overflow-y: auto;
        flex: 1;
        padding: var(--space-sm) 0;
      }
      .group-member-row {
        display: flex;
        align-items: center;
        gap: var(--space-md);
        padding: var(--space-sm) var(--space-lg);
        border-bottom: 1px solid var(--border-color, rgba(255,255,255,0.06));
      }
      .group-member-actions {
        flex-shrink: 0;
        display: flex;
        align-items: center;
      }
      .group-member-remove-btn {
        font-size: var(--font-xs);
        padding: 6px 10px;
        border-radius: var(--radius-sm);
        border: 1px solid var(--border-color, rgba(255,255,255,0.12));
        background: transparent;
        color: var(--text-secondary);
        cursor: pointer;
        white-space: nowrap;
      }
      .group-member-remove-btn:hover {
        color: #f44336;
        border-color: rgba(244, 67, 54, 0.45);
      }
      .group-member-row.group-member-tier-0 {
        background: rgba(76, 175, 80, 0.08);
      }
      .group-member-avatar-wrap {
        position: relative;
        flex-shrink: 0;
      }
      .group-member-avatar-wrap img {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        object-fit: cover;
      }
      .presence-dot {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 11px;
        height: 11px;
        border-radius: 50%;
        border: 2px solid var(--bg-primary, #1a1a1a);
        background: #9e9e9e;
      }
      .presence-dot-on {
        background: #4caf50;
      }
      .presence-dot-busy {
        background: #f44336;
      }
      .group-member-info {
        flex: 1;
        min-width: 0;
      }
      .group-member-name-row {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .group-member-name {
        font-weight: 600;
        font-size: var(--font-sm);
        color: var(--text-primary);
      }
      .group-member-role-badge {
        font-size: var(--font-xs);
        background: var(--bg-tertiary);
        color: var(--text-secondary);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
      }
      .group-member-status {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        margin-top: 2px;
      }
      .group-member-row.group-member-row-interactive {
        cursor: context-menu;
      }
      .group-member-context-menu {
        min-width: 180px;
        padding: var(--space-xs) 0;
        background: var(--bg-secondary, #2a2a2a);
        border: 1px solid var(--border-color, rgba(255,255,255,0.12));
        border-radius: var(--radius-md, 8px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.35);
      }
      .group-member-ctx-item {
        display: block;
        width: 100%;
        text-align: left;
        padding: var(--space-sm) var(--space-md);
        border: none;
        background: transparent;
        color: var(--text-primary);
        font-size: var(--font-sm);
        cursor: pointer;
      }
      .group-member-ctx-item:hover {
        background: var(--bg-tertiary, rgba(255,255,255,0.06));
      }
      .group-members-footer {
        padding: var(--space-md) var(--space-lg) var(--space-lg);
        border-top: 1px solid var(--bg-tertiary);
      }
      .group-members-footer.hidden {
        display: none;
      }
      .group-close-btn {
        width: 100%;
        padding: 8px 12px;
        border-radius: var(--radius-sm);
        border: 1px solid rgba(207, 102, 121, 0.75);
        background: transparent;
        color: var(--text-danger, #cf6679);
        cursor: pointer;
        font-size: var(--font-sm);
      }
      .group-close-btn:disabled {
        opacity: 0.6;
        cursor: default;
      }
      .group-close-hint {
        margin: 8px 0 0;
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
    `
    document.head.appendChild(st)
  }

  removeGroupMemberContextMenu () {
    const m = document.querySelector('.group-member-context-menu')
    if (m) {
      m.remove()
    }
    if (this._onGroupMemberCtxDoc) {
      document.removeEventListener('mousedown', this._onGroupMemberCtxDoc, true)
      this._onGroupMemberCtxDoc = null
    }
  }

  openGroupMemberContextMenu (clientX, clientY, entry, ctxOpts = null) {
    this.removeGroupMemberContextMenu()
    const menu = document.createElement('div')
    menu.className = 'group-member-context-menu'
    menu.setAttribute('role', 'menu')

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'group-member-ctx-item'
    btn.setAttribute('role', 'menuitem')
    btn.textContent = 'Özel mesaj gönder'
    btn.addEventListener('click', async (ev) => {
      ev.stopPropagation()
      this.removeGroupMemberContextMenu()
      await this.startPrivateChatWithUser(entry.uid)
    })
    menu.appendChild(btn)

    if (ctxOpts && ctxOpts.showRemoveFromGroup && typeof ctxOpts.onRemoveFromGroup === 'function') {
      const removeCtx = document.createElement('button')
      removeCtx.type = 'button'
      removeCtx.className = 'group-member-ctx-item'
      removeCtx.setAttribute('role', 'menuitem')
      removeCtx.textContent = 'Gruptan çıkar'
      removeCtx.addEventListener('click', async (ev) => {
        ev.stopPropagation()
        this.removeGroupMemberContextMenu()
        await ctxOpts.onRemoveFromGroup(entry)
      })
      menu.appendChild(removeCtx)
    }
    document.body.appendChild(menu)

    menu.style.position = 'fixed'
    menu.style.left = `${clientX}px`
    menu.style.top = `${clientY}px`
    menu.style.zIndex = '10050'

    const r = menu.getBoundingClientRect()
    let left = clientX
    let top = clientY
    if (left + r.width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - r.width - 8)
    }
    if (top + r.height > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - r.height - 8)
    }
    if (left < 8) {
      left = 8
    }
    if (top < 8) {
      top = 8
    }
    menu.style.left = `${left}px`
    menu.style.top = `${top}px`

    this._onGroupMemberCtxDoc = (ev) => {
      if (!menu.contains(ev.target)) {
        this.removeGroupMemberContextMenu()
      }
    }
    setTimeout(() => {
      document.addEventListener('mousedown', this._onGroupMemberCtxDoc, true)
    }, 0)
  }

  async startPrivateChatWithUser (otherUserId) {
    if (!otherUserId || otherUserId === this.currentUser?.uid) {
      return
    }
    const chatList = typeof window !== 'undefined' ? window.app?.chatList : null
    if (!chatList || typeof chatList.startPrivateChat !== 'function') {
      alert('Sohbet listesi hazır değil.')
      return
    }
    document.querySelector('.group-members-modal')?.remove()
    this.removeGroupMemberContextMenu()
    try {
      await chatList.startPrivateChat(otherUserId)
      const sidebar = document.querySelector('.sidebar')
      if (sidebar) {
        sidebar.classList.remove('open')
      }
    } catch (err) {
      alert('Özel sohbet açılırken bir hata oluştu.')
    }
  }

  async closeGroupCompletelyFromUi (chatDetails, button, closeModal) {
    const promptInfo = describeGroupClose(chatDetails?.name || chatDetails?.groupName)
    if (!window.confirm(promptInfo.confirm)) {
      return
    }
    const typed = window.prompt(promptInfo.prompt)
    if (typed == null) {
      return
    }
    if (typed.trim() !== promptInfo.name) {
      alert('Grup adı eşleşmedi. Kapatma iptal edildi.')
      return
    }
    const chatId = chatDetails.id
    if (button) {
      button.disabled = true
      button.textContent = 'Kapatılıyor…'
    }
    if (this.currentChatId === chatId) {
      this.currentChatId = null
      this.clearChat()
    }
    try {
      const result = await chatService.closeGroupCompletely(chatId, this.currentUser.uid, (progress) => {
        if (button) {
          button.textContent = `Kapatılıyor… ${progress.deletedDocs || 0} mesaj`
        }
      })
      if (typeof closeModal === 'function') {
        closeModal()
      }
      document.dispatchEvent(new CustomEvent('chat-archived', { detail: { chatId } }))
      alert(`«${result.groupName}» kapatıldı. ${result.deletedMessages} mesaj ve ${result.deletedFiles} dosya silindi.`)
    } catch (error) {
      if (button) {
        button.disabled = false
        button.textContent = 'Grubu tamamen kapat'
      }
      alert(error.message || 'Grup kapatılamadı.')
    }
  }

  /**
   * Grup başlığı "Daha Fazla" — üyeleri aktiflik sırasına göre listeler
   */
  async showGroupMembersModal (chatDetails) {
    const existing = document.querySelector('.group-members-modal')
    if (existing) {
      existing.remove()
    }
    this.removeGroupMemberContextMenu()
    this.ensureGroupMembersModalStyles()

    const modal = document.createElement('div')
    modal.className = 'modal group-members-modal'
    modal.setAttribute('role', 'dialog')
    modal.setAttribute('aria-modal', 'true')
    modal.setAttribute('aria-labelledby', 'group-members-modal-title')

    const inner = document.createElement('div')
    inner.className = 'modal-content group-members-modal-content'
    inner.innerHTML = `
      <div class="modal-header">
        <h3 class="modal-title" id="group-members-modal-title">Grup üyeleri</h3>
        <button type="button" class="modal-close-btn group-members-close" aria-label="Kapat">&times;</button>
      </div>
      <p class="group-members-subtitle"></p>
      <div class="modal-body group-members-modal-body">
        <div class="group-members-loading">Üyeler yükleniyor…</div>
        <div class="group-members-list hidden"></div>
      </div>
      <div class="group-members-footer hidden">
        <button type="button" class="group-close-btn">Grubu tamamen kapat</button>
        <p class="group-close-hint">Mesajlar, dosyalar ve grup kaydı silinir. Bu işlem geri alınamaz.</p>
      </div>
    `
    modal.appendChild(inner)
    document.body.appendChild(modal)

    const subtitle = inner.querySelector('.group-members-subtitle')
    subtitle.textContent = chatDetails.name || 'Grup sohbeti'

    const closeModal = () => {
      this.removeGroupMemberContextMenu()
      if (modal.parentNode) {
        modal.parentNode.removeChild(modal)
      }
    }
    inner.querySelector('.group-members-close').addEventListener('click', closeModal)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal()
      }
    })

    const loadingEl = inner.querySelector('.group-members-loading')
    const listEl = inner.querySelector('.group-members-list')
    const chatId = chatDetails.id
    const isAnnouncement = !!chatDetails.isAnnouncement
    const ids = [...new Set(chatDetails.participants || chatDetails.users || [])]
    const memberCount = ids.length

    let canRemoveOthers = false
    let isAdmin = false
    try {
      const me = await userService.getUserProfile(this.currentUser.uid)
      canRemoveOthers = userCanRemoveOthersFromGroup(me)
      isAdmin = isAdminProfile(me)
    } catch (e) {
      canRemoveOthers = false
      isAdmin = false
    }

    if (isAdmin) {
      const footer = inner.querySelector('.group-members-footer')
      const closeBtn = inner.querySelector('.group-close-btn')
      footer.classList.remove('hidden')
      closeBtn.addEventListener('click', () => this.closeGroupCompletelyFromUi(chatDetails, closeBtn, closeModal))
    }

    const runRemove = async (targetUid, isSelf) => {
      const msg = isSelf
        ? 'Bu gruptan ayrılmak istediğinize emin misiniz?'
        : 'Bu üyeyi gruptan çıkarmak istediğinize emin misiniz?'
      if (!window.confirm(msg)) {
        return
      }
      try {
        await chatService.removeUserFromGroup(chatId, targetUid, this.currentUser.uid)
        if (isSelf) {
          closeModal()
          if (this.currentChatId === chatId) {
            this.currentChatId = null
            this.clearChat()
          }
        } else {
          const fresh = await chatService.getChatDetails(chatId)
          await this.showGroupMembersModal(fresh)
        }
      } catch (err) {
        const m = err && err.message ? err.message : 'İşlem tamamlanamadı.'
        alert(m)
      }
    }

    try {
      const raw = await Promise.all(ids.map(async (uid) => {
        try {
          // OPTIMIZASYON: Grup üyeleri modalında önbelleği kullan (skipCache kaldırıldı).
          // Önceden her açılışta her üye için ayrı okuma yapılıyordu (50 kişilik grup = 50 okuma).
          // Üye listesindeki son görülme birkaç dakika gecikmeli olsa da kritik değil.
          const profile = await userService.getUserProfile(uid)
          return { uid, profile }
        } catch (err) {
          return { uid, profile: null }
        }
      }))

      const entries = raw.map(({ uid, profile }) => {
        const displayName = profile
          ? (profile.displayName || profile.name || profile.userCode || uid.substring(0, 8))
          : `Kullanıcı ${uid.substring(0, 6)}`
        const meta = profile
          ? presenceMetaFromLastSeenOnly(profile)
          : { label: 'Profil yüklenemedi', tier: 2, lastMs: 0, modalDotClass: 'presence-dot' }
        const picSeed = profile
          ? (profile.userCode || profile.code || profile.displayName || profile.name || profile.email || displayName)
          : displayName
        const hasPhoto = profile?.photoURL && String(profile.photoURL).trim() !== ''
        const photoURL = hasPhoto ? profile.photoURL : generateProfilePicture(picSeed)
        const userRole = (profile?.userRole || profile?.role || '').trim()
        const isSelf = uid === this.currentUser?.uid
        return { uid, displayName, meta, photoURL, userRole, isSelf, picSeed }
      })

      entries.sort((a, b) => {
        if (a.meta.tier !== b.meta.tier) {
          return a.meta.tier - b.meta.tier
        }
        return b.meta.lastMs - a.meta.lastMs
      })

      listEl.innerHTML = ''
      entries.forEach((entry) => {
        const row = document.createElement('div')
        row.className = `group-member-row group-member-tier-${entry.meta.tier}`

        const avWrap = document.createElement('div')
        avWrap.className = 'group-member-avatar-wrap'
        const dot = document.createElement('span')
        dot.className = entry.meta.modalDotClass || 'presence-dot'
        const img = document.createElement('img')
        img.src = entry.photoURL
        img.alt = entry.displayName || ''
        img.referrerPolicy = 'no-referrer'
        img.addEventListener('error', () => {
          img.onerror = null
          img.src = generateProfilePicture(entry.picSeed || entry.displayName || 'Kullanıcı')
        }, { once: true })
        avWrap.appendChild(img)
        avWrap.appendChild(dot)

        const info = document.createElement('div')
        info.className = 'group-member-info'
        const nameRow = document.createElement('div')
        nameRow.className = 'group-member-name-row'
        const nameEl = document.createElement('div')
        nameEl.className = 'group-member-name'
        nameEl.textContent = entry.isSelf ? `${entry.displayName} (Sen)` : entry.displayName
        nameRow.appendChild(nameEl)
        if (entry.userRole) {
          const badge = document.createElement('span')
          badge.className = 'group-member-role-badge'
          badge.textContent = entry.userRole
          nameRow.appendChild(badge)
        }
        const statusEl = document.createElement('div')
        statusEl.className = 'group-member-status'
        statusEl.textContent = entry.meta.label
        info.appendChild(nameRow)
        info.appendChild(statusEl)

        row.appendChild(avWrap)
        row.appendChild(info)

        const showSelfLeave = entry.isSelf && memberCount > 1
        const showKick = !entry.isSelf && canRemoveOthers && !isAnnouncement && memberCount > 1
        if (showSelfLeave || showKick) {
          const actions = document.createElement('div')
          actions.className = 'group-member-actions'
          const removeBtn = document.createElement('button')
          removeBtn.type = 'button'
          removeBtn.className = 'group-member-remove-btn'
          removeBtn.textContent = entry.isSelf ? 'Gruptan ayrıl' : 'Gruptan çıkar'
          removeBtn.addEventListener('click', async (ev) => {
            ev.stopPropagation()
            await runRemove(entry.uid, entry.isSelf)
          })
          actions.appendChild(removeBtn)
          row.appendChild(actions)
        }

        listEl.appendChild(row)

        if (!entry.isSelf) {
          row.classList.add('group-member-row-interactive')
          row.addEventListener('contextmenu', (e) => {
            e.preventDefault()
            this.openGroupMemberContextMenu(e.clientX, e.clientY, entry, {
              showRemoveFromGroup: showKick,
              onRemoveFromGroup: async (ent) => {
                await runRemove(ent.uid, false)
              }
            })
          })
        }
      })

      if (entries.length === 0) {
        loadingEl.textContent = 'Bu grupta üye bulunamadı.'
      } else {
        loadingEl.classList.add('hidden')
        listEl.classList.remove('hidden')
      }
    } catch (err) {
      loadingEl.textContent = 'Üyeler yüklenirken bir hata oluştu.'
      console.warn('Grup üyeleri modalı:', err)
    }
  }
  
  // Yeni modal: Grup sohbetine kullanıcı ekleme (sohbet oluşturma ile aynı mantık)
  async showAddUserToGroupModal(chatDetails) {
    // Mevcut üyeler
    const currentMembers = chatDetails.participants || chatDetails.users || [];
    let selectedUsers = [];
    // Modal HTML
    const modalHtml = `
      <div class="modal-content">
        <div class="modal-header">
          <h3>Gruba Kullanıcı Ekle</h3>
          <button class="modal-close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <label for="group-user-search" class="input-label">Kullanıcı Ara</label>
            <input type="text" id="group-user-search" class="input-field" placeholder="Kullanıcı adı, kodu veya e-posta girin...">
          </div>
          <div class="user-list" id="group-user-list"></div>
          <div class="selected-users" id="selected-group-users"></div>
        </div>
        <div class="modal-footer">
          <button id="add-users-btn" class="btn primary-btn">Ekle</button>
          <button class="btn secondary-btn modal-close-btn">İptal</button>
        </div>
      </div>
    `;
    // Modal oluştur
    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.innerHTML = modalHtml;
    document.body.appendChild(modal);
    // Modal kapatma işlevi
    const closeModal = () => { document.body.removeChild(modal); };
    modal.querySelectorAll('.modal-close-btn').forEach(btn => {
      btn.addEventListener('click', closeModal);
    });
    modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
    // Debounce fonksiyonu
    const debounce = (func, delay) => {
      let debounceTimer;
      return function(...args) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => func.apply(this, args), delay);
      };
    };
    // Kullanıcı arama işlevi
    const userSearchInput = modal.querySelector('#group-user-search');
    const groupUserList = modal.querySelector('#group-user-list');
    const selectedGroupUsers = modal.querySelector('#selected-group-users');
    // Seçili kullanıcıları güncelleyen fonksiyon
    const updateSelectedUsers = () => {
      selectedGroupUsers.innerHTML = '';
      if (selectedUsers.length === 0) {
        selectedGroupUsers.innerHTML = '<div class="no-users">Henüz kullanıcı seçilmedi.</div>';
        return;
      }
      selectedUsers.forEach(user => {
        const userEl = document.createElement('div');
        userEl.className = 'user-item selected';
        userEl.dataset.userId = user.id;
        let displayName = user.userCode || user.displayName || user.email || user.id.substring(0, 6);
        userEl.innerHTML = `
          <div class="user-avatar"><img src="${user.photoURL || 'https://www.bilcad.com/assets/vertical-no-tagline-transparent-250x250-BmXwcde2.svg'}" alt="${displayName}" /></div>
          <div class="user-info"><div class="user-name">${displayName}</div></div>
          <div class="remove-user-btn">×</div>
        `;
        userEl.querySelector('.remove-user-btn').addEventListener('click', (e) => {
          e.stopPropagation();
          selectedUsers = selectedUsers.filter(u => u.id !== user.id);
          updateSelectedUsers();
        });
        selectedGroupUsers.appendChild(userEl);
      });
    };
    userSearchInput.addEventListener('input', debounce(async (e) => {
      const searchValue = e.target.value.trim();
      if (searchValue.length < 2) {
        groupUserList.innerHTML = '<div class="no-users">En az 2 karakter girin.</div>';
        return;
      }
      try {
        const searchResults = await userService.findUsersByCodeOrEmail(searchValue);
        // Sadece mevcut üyeler hariç olanları göster
        const filteredUsers = searchResults.filter(user => !currentMembers.includes(user.id) && !selectedUsers.some(u => u.id === user.id));
        if (filteredUsers.length === 0) {
          groupUserList.innerHTML = '<div class="no-users">Kullanıcı bulunamadı.</div>';
          return;
        }
        groupUserList.innerHTML = '';
        filteredUsers.forEach(user => {
          const userEl = document.createElement('div');
          userEl.className = 'user-item';
          userEl.dataset.userId = user.id;
          let displayName = user.userCode || user.displayName || user.email || user.id.substring(0, 6);
          userEl.innerHTML = `
            <div class="user-avatar"><img src="${user.photoURL || 'https://www.bilcad.com/assets/vertical-no-tagline-transparent-250x250-BmXwcde2.svg'}" alt="${displayName}" /></div>
            <div class="user-info"><div class="user-name">${displayName}</div></div>
            <div class="add-user-btn">+</div>
          `;
          userEl.addEventListener('click', () => {
            selectedUsers.push(user);
            updateSelectedUsers();
            groupUserList.removeChild(userEl);
            userSearchInput.value = '';
            if (groupUserList.children.length === 0) {
              groupUserList.innerHTML = '<div class="no-users">Kullanıcı aramak için yukarıdaki alanı kullanın.</div>';
            }
          });
          groupUserList.appendChild(userEl);
        });
      } catch (error) {
        groupUserList.innerHTML = '<div class="no-users">Arama sırasında bir hata oluştu.</div>';
      }
    }, 500));
    // Başlangıçta seçili kullanıcıları göster
    updateSelectedUsers();
    // Ekle butonu
    const addUsersBtn = modal.querySelector('#add-users-btn');
    addUsersBtn.addEventListener('click', async () => {
      if (selectedUsers.length === 0) {
        alert('Lütfen en az bir kullanıcı seçin.');
        return;
      }
      try {
        const userIds = selectedUsers.map(u => u.id);
        await chatService.addUsersToGroup(chatDetails.id, userIds);
        alert('Kullanıcılar başarıyla gruba eklendi!');
        closeModal();
        // Sohbet başlığını ve UI'ı güncelle
        this.loadChat(chatDetails.id);
      } catch (error) {
        alert('Kullanıcı eklenirken hata oluştu.');
      }
    });
  }
  
  // Dosya boyutunu formatlama yardımcı fonksiyonu
  formatFileSize(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }
  
  // Dosya uzantısını alma fonksiyonu
  getFileExtension(filename) {
    return filename.split('.').pop().toLowerCase();
  }
  
  // Dosya türüne göre ikon belirleme fonksiyonu
  getFileIcon(extension) {
    const iconMap = {
      // Images
      jpg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
      jpeg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
      png: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
      gif: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
      
      // Documents
      pdf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>',
      doc: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>',
      docx: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>',
      
      // Videos
      mp4: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>',
      avi: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>',
      mov: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>',
    };

    return iconMap[extension] || '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>';
  }
  
    // Mesajlara sağ tıklama menüsü ekle
    attachMessageContextMenus() {
      const messageItems = this.messageListContainer.querySelectorAll('.message-item:not(.deleted-message)');
      
      messageItems.forEach(item => {
        const senderId = item.getAttribute('data-sender-id');
        const messageId = item.getAttribute('data-id');
        
        // Sağ tıklama (desktop)
        item.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          this.showMessageContextMenu(e.clientX, e.clientY, messageId, item);
        });
        
        // Long press (mobil) için
        let pressTimer;
        let touchStartX;
        let touchStartY;
        
        item.addEventListener('touchstart', (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          
          // 500ms sonra context menu aç
          pressTimer = setTimeout(() => {
            // Vibration API varsa hafif titreşim
            if ('vibrate' in navigator) {
              navigator.vibrate(50);
            }
            
            const touch = e.touches[0];
            this.showMessageContextMenu(touch.clientX, touch.clientY, messageId, item);
          }, 500);
        }, { passive: true });
        
        item.addEventListener('touchmove', (e) => {
          // Parmak hareket ederse timer'ı iptal et
          const touch = e.touches[0];
          const moveX = Math.abs(touch.clientX - touchStartX);
          const moveY = Math.abs(touch.clientY - touchStartY);
          
          if (moveX > 10 || moveY > 10) {
            clearTimeout(pressTimer);
          }
        }, { passive: true });
        
        item.addEventListener('touchend', () => {
          clearTimeout(pressTimer);
        }, { passive: true });
        
        item.addEventListener('touchcancel', () => {
          clearTimeout(pressTimer);
        }, { passive: true });
      });
      
      // Yanıtlanan mesaj referanslarına tıklama eventi ekle
      this.attachReplyClickHandlers();
    }
    
    // Yanıt referanslarına tıklama eventi ekle
    attachReplyClickHandlers() {
      const replyMessages = this.messageListContainer.querySelectorAll('.replied-message[data-reply-to]');
      
      replyMessages.forEach(replyElement => {
        replyElement.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const replyToMessageId = replyElement.getAttribute('data-reply-to');
          
          // Yükleniyor göstergesi göster
          const loadingNotification = document.createElement('div');
          loadingNotification.className = 'message-search-notification';
          loadingNotification.innerHTML = `
            <div class="loading-spinner-small"></div>
            <span>Mesaj aranıyor...</span>
          `;
          loadingNotification.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: var(--bg-secondary);
            color: var(--text-primary);
            padding: 12px 24px;
            border-radius: var(--radius-md);
            z-index: 10000;
            display: flex;
            align-items: center;
            gap: 10px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            animation: slideDown 0.3s ease;
          `;
          document.body.appendChild(loadingNotification);
          
          // Mesajı bul
          const found = await this.scrollToMessage(replyToMessageId);
          
          // Yükleniyor göstergesini kaldır
          loadingNotification.remove();
        });
      });
    }
    
    // Belirli bir mesaja scroll yap
    async scrollToMessage(messageId, retryCount = 0, maxRetries = 10) {
      const targetMessage = this.messageListContainer.querySelector(`[data-id="${messageId}"]`);
      
      if (targetMessage) {
        // Mesajı vurgula
        targetMessage.style.backgroundColor = 'var(--bg-accent-light)';
        targetMessage.style.border = '2px solid var(--bg-accent)';
        targetMessage.style.borderRadius = 'var(--radius-md)';
        targetMessage.style.transition = 'all 0.3s ease';
        
        // Mesaja scroll yap
        targetMessage.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // 3 saniye sonra vurguyu kaldır
        setTimeout(() => {
          targetMessage.style.backgroundColor = '';
          targetMessage.style.border = '';
          targetMessage.style.borderRadius = '';
        }, 3000);
        
        return true; // Mesaj bulundu
      } else {
        // Mesaj henüz yüklenmemişse
        if (retryCount >= maxRetries) {
          console.warn('Yanıtlanan mesaj bulunamadı (max retry aşıldı):', messageId);
          
          // Kullanıcıya bilgi ver
          const notification = document.createElement('div');
          notification.className = 'message-not-found-notification';
          notification.textContent = 'Yanıtlanan mesaj bulunamadı (çok eski olabilir)';
          notification.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: var(--bg-danger);
            color: white;
            padding: 12px 24px;
            border-radius: var(--radius-md);
            z-index: 10000;
            animation: slideDown 0.3s ease;
          `;
          document.body.appendChild(notification);
          
          setTimeout(() => {
            notification.remove();
          }, 3000);
          
          return false; // Mesaj bulunamadı
        }
        
        // Daha fazla mesaj yüklenebilir mi kontrol et
        if (!this.hasMoreMessages) {
          console.warn('Yanıtlanan mesaj bulunamadı (daha fazla mesaj yok):', messageId);
          
          // Kullanıcıya bilgi ver
          const notification = document.createElement('div');
          notification.className = 'message-not-found-notification';
          notification.textContent = 'Yanıtlanan mesaj bulunamadı';
          notification.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: var(--bg-danger);
            color: white;
            padding: 12px 24px;
            border-radius: var(--radius-md);
            z-index: 10000;
            animation: slideDown 0.3s ease;
          `;
          document.body.appendChild(notification);
          
          setTimeout(() => {
            notification.remove();
          }, 3000);
          
          return false; // Mesaj bulunamadı
        }
        
        // Eski mesajları yükle ve tekrar dene
        
        try {
          await this.loadOlderMessages();
          
          // Biraz bekle (render için)
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Recursive olarak tekrar dene
          return await this.scrollToMessage(messageId, retryCount + 1, maxRetries);
        } catch (error) {
          console.error('Eski mesajlar yüklenirken hata:', error);
          return false;
        }
      }
    }
  
  // Mesaj context menu göster
  showMessageContextMenu(x, y, messageId, messageElement) {
    // Varolan menüyü kaldır
    const existingMenu = document.querySelector('.message-context-menu');
    if (existingMenu) {
      existingMenu.remove();
    }
    
    const message = this.messages.find(m => m.id === messageId);
    const isMine = message && message.senderId === this.currentUser.uid;
    
    // Admin/control yetkisi kontrolü
    const currentUserRole = this.currentUser.role || this.currentUser.userRole;
    const isAdminOrControl = currentUserRole === 'admin' || currentUserRole === 'control';
    
    const menu = document.createElement('div');
    menu.className = 'message-context-menu';
    menu.innerHTML = `
      <div class="context-menu-item" data-action="reply">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 14 4 9 9 4"></polyline>
          <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
        </svg>
        Yanıtla
      </div>
      ${isMine ? `
        <div class="context-menu-item" data-action="edit">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          Düzenle
        </div>
        <div class="context-menu-item" data-action="delete">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          Sil
        </div>
      ` : ''}
      ${!isMine && isAdminOrControl ? `
        <div class="context-menu-item admin-delete" data-action="admin-delete">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          Sil (Admin)
        </div>
      ` : ''}
    `;
    
    // Ekran sınırlarını kontrol et
    const menuWidth = 200; // Menü genişliği
    const menuHeight = 120; // Menü yüksekliği
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // X pozisyonu ayarla (sağ kenardan taşmasın)
    let finalX = x;
    if (x + menuWidth > screenWidth) {
      finalX = screenWidth - menuWidth - 10; // 10px margin
    }
    if (finalX < 10) finalX = 10; // Sol kenardan da taşmasın
    
    // Y pozisyonu ayarla (alt kenardan taşmasın)
    let finalY = y;
    if (y + menuHeight > screenHeight) {
      finalY = screenHeight - menuHeight - 10; // 10px margin
    }
    if (finalY < 10) finalY = 10; // Üst kenardan da taşmasın
    
    menu.style.position = 'fixed';
    menu.style.left = `${finalX}px`;
    menu.style.top = `${finalY}px`;
    menu.style.zIndex = '1000';
    document.body.appendChild(menu);
    
    // Menü dışına tıklandığında kapat
    const closeMenu = (e) => {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      }
    };
    setTimeout(() => document.addEventListener('click', closeMenu), 0);
    
    // Reply action
    menu.querySelector('[data-action="reply"]').addEventListener('click', () => {
      this.setReplyTo(messageId);
      menu.remove();
    });
    
    // Edit action (sadece kendi mesajlarında var)
    const editBtn = menu.querySelector('[data-action="edit"]');
    if (editBtn) {
      editBtn.addEventListener('click', () => {
        this.editMessage(messageId, messageElement);
        menu.remove();
      });
    }
    
    // Delete action (sadece kendi mesajlarında var)
    const deleteBtn = menu.querySelector('[data-action="delete"]');
    if (deleteBtn) {
      deleteBtn.addEventListener('click', async () => {
        menu.remove();
        if (confirm('Bu mesajı silmek istediğinizden emin misiniz?')) {
          try {
            await chatService.deleteMessage(this.currentChatId, messageId, this.currentUser.uid);
          } catch (error) {
            alert(error.message || 'Mesaj silinemedi');
          }
        }
      });
    }
    
    // Admin delete action (admin/control için başkalarının mesajlarını silme)
    const adminDeleteBtn = menu.querySelector('[data-action="admin-delete"]');
    if (adminDeleteBtn) {
      adminDeleteBtn.addEventListener('click', async () => {
        menu.remove();
        if (confirm('Bu mesajı admin yetkisiyle silmek istediğinizden emin misiniz?')) {
          try {
            await chatService.deleteMessage(this.currentChatId, messageId, this.currentUser.uid);
          } catch (error) {
            alert(error.message || 'Mesaj silinemedi');
          }
        }
      });
    }
    
    // Stil ekle
    if (!document.querySelector('style#message-context-menu-styles')) {
      const style = document.createElement('style');
      style.id = 'message-context-menu-styles';
      style.textContent = `
        .message-context-menu {
          background: var(--bg-primary);
          border: 1px solid var(--bg-tertiary);
          border-radius: var(--radius-lg);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
          padding: var(--space-xs);
          z-index: 10000;
          min-width: 180px;
          backdrop-filter: blur(10px);
          animation: contextMenuAppear 0.2s ease-out;
        }
        
        @keyframes contextMenuAppear {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-5px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        .context-menu-item {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          cursor: pointer;
          border-radius: var(--radius-md);
          transition: all 0.2s ease;
          font-size: var(--font-sm);
          font-weight: 500;
          color: var(--text-primary);
          position: relative;
        }
        
        .context-menu-item:hover {
          background: linear-gradient(135deg, var(--bg-hover), var(--bg-tertiary));
          transform: translateX(2px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .context-menu-item svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }
        
        .context-menu-item:hover svg {
          transform: scale(1.1);
        }
        
        .context-menu-item[data-action="reply"] {
          color: var(--bg-accent);
        }
        
        .context-menu-item[data-action="edit"] {
          color: var(--text-warning);
        }
        
        .context-menu-item[data-action="delete"] {
          color: var(--text-danger);
        }
        
        .context-menu-item[data-action="admin-delete"] {
          color: var(--text-danger);
          background: linear-gradient(135deg, rgba(220, 38, 38, 0.1), rgba(185, 28, 28, 0.1));
          border-left: 3px solid var(--text-danger);
        }
        
        .context-menu-item[data-action="admin-delete"]:hover {
          background: linear-gradient(135deg, rgba(220, 38, 38, 0.2), rgba(185, 28, 28, 0.2));
          transform: translateX(2px);
        }
        
        .edited-label {
          font-size: 11px;
          color: var(--text-tertiary);
          margin-right: var(--space-xs);
        }
        
        .deleted-message {
          opacity: 0.6;
          font-style: italic;
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  // Mesaj düzenle
  async editMessage(messageId, messageElement) {
    // Mevcut mesaj içeriğini al
    const message = this.messages.find(m => m.id === messageId);
    if (!message || message.type !== 'text') {
      alert('Sadece text mesajları düzenleyebilirsiniz');
      return;
    }
    
    const currentContent = message.content;
    
    // Modal göster
    const modalHtml = `
      <div class="edit-message-modal">
        <div class="edit-modal-content">
          <div class="edit-modal-header">
            <h3>Mesajı Düzenle</h3>
            <button class="close-edit-modal">×</button>
          </div>
          <div class="edit-modal-body">
            <textarea id="edit-message-textarea" class="edit-textarea">${currentContent}</textarea>
          </div>
          <div class="edit-modal-footer">
            <button class="btn secondary-btn close-edit-modal">İptal</button>
            <button class="btn primary-btn save-edit-btn">Kaydet</button>
          </div>
        </div>
      </div>
    `;
    
    const modal = document.createElement('div');
    modal.innerHTML = modalHtml;
    document.body.appendChild(modal.firstElementChild);
    
    const editModal = document.querySelector('.edit-message-modal');
    const textarea = document.getElementById('edit-message-textarea');
    
    // Focus ve select
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    
    // Close handlers
    const closeModal = () => editModal.remove();
    editModal.querySelectorAll('.close-edit-modal').forEach(btn => {
      btn.addEventListener('click', closeModal);
    });
    
    // Save handler
    editModal.querySelector('.save-edit-btn').addEventListener('click', async () => {
      const newContent = textarea.value.trim();
      if (!newContent) {
        alert('Mesaj boş olamaz');
        return;
      }
      
      if (newContent === currentContent) {
        closeModal();
        return;
      }
      
      try {
        await chatService.editMessage(this.currentChatId, messageId, newContent, this.currentUser.uid);
        closeModal();
      } catch (error) {
        alert(error.message || 'Mesaj düzenlenemedi');
      }
    });
    
    // Stil ekle
    if (!document.querySelector('style#edit-message-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'edit-message-modal-styles';
      style.textContent = `
        .edit-message-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }
        
        .edit-modal-content {
          background: var(--bg-primary);
          border-radius: var(--radius-lg);
          width: 90%;
          max-width: 500px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        }
        
        .edit-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-md);
          border-bottom: 1px solid var(--bg-tertiary);
        }
        
        .edit-modal-header h3 {
          margin: 0;
          font-size: var(--font-md);
        }
        
        .close-edit-modal {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--text-secondary);
        }
        
        .edit-modal-body {
          padding: var(--space-md);
        }
        
        .edit-textarea {
          width: 100%;
          min-height: 100px;
          padding: var(--space-sm);
          border: 1px solid var(--bg-tertiary);
          border-radius: var(--radius-md);
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-family: inherit;
          font-size: var(--font-sm);
          resize: vertical;
        }
        
        .edit-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-sm);
          padding: var(--space-md);
          border-top: 1px solid var(--bg-tertiary);
        }
        
        .btn {
          padding: var(--space-sm) var(--space-md);
          border: none;
          border-radius: var(--radius-md);
          font-size: var(--font-sm);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 80px;
        }
        
        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }
        
        .btn:active {
          transform: translateY(0);
        }
        
        .primary-btn {
          background: var(--bg-accent);
          color: white;
        }
        
        .primary-btn:hover {
          background: var(--bg-accent-dark);
          box-shadow: 0 4px 12px rgba(var(--bg-accent-rgb), 0.3);
        }
        
        .secondary-btn {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--bg-tertiary);
        }
        
        .secondary-btn:hover {
          background: var(--bg-hover);
          border-color: var(--bg-hover);
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  // Yanıtlanacak mesajı ayarla
  setReplyTo(messageId) {
    const message = this.messages.find(m => m.id === messageId);
    if (!message) return;
    
    this.replyingTo = {
      id: messageId,
      content: message.content || 'Medya',
      senderId: message.senderId,
      type: message.type,
      fileURL: message.fileURL || null
    };
    
    // Yanıt gösterge alanını oluştur
    this.showReplyIndicator();
    
    // Textarea'ya focus yap
    const textarea = document.getElementById('message-textarea');
    if (textarea) {
      textarea.focus();
    }
  }
  
  // Yanıt göstergesini göster
  async showReplyIndicator() {
    if (!this.replyingTo) return;
    
    // Kullanıcı adını al
    let senderName = 'Kullanıcı';
    try {
      const user = await userService.getUserProfile(this.replyingTo.senderId);
      senderName = user?.displayName || user?.userCode || 'Kullanıcı';
    } catch (error) {
      console.warn('Yanıtlanan kullanıcı adı alınamadı');
    }
    
    // Yanıt göstergesini input alanının üstüne ekle
    let replyIndicator = this.messageInputContainer.querySelector('.reply-indicator');
    if (!replyIndicator) {
      replyIndicator = document.createElement('div');
      replyIndicator.className = 'reply-indicator';
      this.messageInputContainer.insertBefore(replyIndicator, this.messageInputContainer.firstChild);
    }
    
    // İçerik tipine göre önizleme
    let preview = '';
    if (this.replyingTo.type === 'image') {
      if (this.replyingTo.fileURL) {
        preview = `
          <div class="reply-image-preview">
            <img src="${this.replyingTo.fileURL}" alt="Resim önizlemesi" class="reply-thumbnail">
            <span>📷 Resim</span>
          </div>
        `;
      } else {
        preview = '📷 Resim';
      }
    } else if (this.replyingTo.type === 'video') {
      preview = '🎥 Video';
    } else if (this.replyingTo.type === 'file' || this.replyingTo.type === 'document') {
      preview = '📄 Dosya';
    } else {
      preview = this.replyingTo.content || '';
      // Önizlemeyi kısalt
      if (preview && preview.length > 50) {
        preview = preview.substring(0, 47) + '...';
      }
    }
    
    replyIndicator.innerHTML = `
      <div class="reply-content">
        <div class="reply-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 14 4 9 9 4"></polyline>
            <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
          </svg>
        </div>
        <div class="reply-info">
          <div class="reply-to-user">${senderName}</div>
          <div class="reply-preview">${preview}</div>
        </div>
      </div>
      <button class="cancel-reply-btn">×</button>
    `;
    
    // İptal butonu
    replyIndicator.querySelector('.cancel-reply-btn').addEventListener('click', () => {
      this.cancelReply();
    });
    
    // Stil ekle
    if (!document.querySelector('style#reply-indicator-styles')) {
      const style = document.createElement('style');
      style.id = 'reply-indicator-styles';
      style.textContent = `
        .reply-indicator {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-sm) var(--space-md);
          background: var(--bg-secondary);
          border-left: 3px solid var(--bg-accent);
          margin-bottom: var(--space-sm);
          border-radius: var(--radius-sm);
          animation: slideDown 0.2s ease;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .reply-content {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          flex: 1;
          min-width: 0;
        }
        
        .reply-icon {
          color: var(--bg-accent);
          display: flex;
          align-items: center;
        }
        
        .reply-info {
          flex: 1;
          min-width: 0;
        }
        
        .reply-to-user {
          font-weight: 600;
          font-size: var(--font-sm);
          color: var(--bg-accent);
        }
        
        .reply-preview {
          font-size: var(--font-xs);
          color: var(--text-secondary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .cancel-reply-btn {
          background: none;
          border: none;
          font-size: 24px;
          color: var(--text-tertiary);
          cursor: pointer;
          padding: 0 var(--space-xs);
          transition: color 0.2s ease;
        }
        
        .cancel-reply-btn:hover {
          color: var(--text-primary);
        }
        
        .replied-message {
          background: var(--bg-tertiary);
          border-left: 3px solid var(--bg-accent);
          padding: var(--space-xs) var(--space-sm);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-xs);
          font-size: var(--font-sm);
        }
        
        .message-group.sent .replied-message {
          background: rgba(255, 255, 255, 0.15);
          border-left-color: rgba(255, 255, 255, 0.5);
        }
        
        .replied-user {
          font-weight: 600;
          font-size: var(--font-xs);
          color: var(--bg-accent);
          margin-bottom: 2px;
        }
        
        .message-group.sent .replied-user {
          color: rgba(255, 255, 255, 0.9);
        }
        
        .replied-content {
          font-size: var(--font-xs);
          color: var(--text-secondary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .message-group.sent .replied-content {
          color: rgba(255, 255, 255, 0.7);
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  // Yanıtı iptal et
  cancelReply() {
    this.replyingTo = null;
    const replyIndicator = this.messageInputContainer.querySelector('.reply-indicator');
    if (replyIndicator) {
      replyIndicator.remove();
    }
  }
  
  // @ etiketleme input kontrolü
  async handleMentionInput(e) {
    const textarea = e.target;
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    
    // @ karakterinden önceki metni al
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    // @ bulunamadıysa veya @ çok geride ise kapat
    if (lastAtIndex === -1 || cursorPos - lastAtIndex > 20) {
      this.closeMentionList();
      return;
    }
    
    // @ dan sonraki arama metnini al
    const searchText = textBeforeCursor.substring(lastAtIndex + 1);
    
    // Boşluk varsa mention aramayı iptal et
    if (searchText.includes(' ')) {
      this.closeMentionList();
      return;
    }
    
    // Kullanıcıları filtrele
    await this.showMentionList(searchText, lastAtIndex);
  }
  
  // Mention listesini göster
  async showMentionList(searchText, atPosition) {
    // Kullanıcıları filtrele
    const filteredUsers = [];
    
    // this.mentionUsers artık user objeleri içeriyor
    for (const user of this.mentionUsers) {
      if (!user || user.uid === this.currentUser?.uid) continue; // Kendimizi etiketlemeyelim
      
      const displayName = user.displayName || user.userCode || user.uid.substring(0, 6);
      const userCode = user.userCode || '';
      
      // Arama filtreleme
      if (!searchText || 
          displayName.toLowerCase().includes(searchText.toLowerCase()) ||
          userCode.toLowerCase().includes(searchText.toLowerCase())) {
        filteredUsers.push({
          id: user.uid,
          displayName,
          userCode,
          photoURL: user.photoURL,
          role: user.role
        });
      }
    }
    
    if (filteredUsers.length === 0) {
      this.closeMentionList();
      return;
    }
    
    // Liste yoksa oluştur
    if (!this.mentionList) {
      this.mentionList = document.createElement('div');
      this.mentionList.className = 'mention-list';
      this.messageInputContainer.appendChild(this.mentionList);
    }
    
    // Liste HTML'i - Rol badge'i ekle
    this.mentionList.innerHTML = filteredUsers.map((user, index) => `
      <div class="mention-item ${index === 0 ? 'selected' : ''}" data-user-id="${user.id}" data-user-code="${user.userCode || user.displayName}">
        <div class="mention-avatar">
          ${user.photoURL ? `<img src="${user.photoURL}" alt="${user.displayName}">` : `<div class="avatar-placeholder">${user.displayName.charAt(0)}</div>`}
        </div>
        <div class="mention-info">
          <div class="mention-name">
            ${user.displayName}
            ${user.role && user.role !== 'register' ? `<span class="mention-role">${user.role}</span>` : ''}
          </div>
          ${user.userCode ? `<div class="mention-code">@${user.userCode}</div>` : ''}
        </div>
      </div>
    `).join('');
    
    this.mentionList.style.display = 'block';
    this.currentMention = { searchText, atPosition };
    
    // Click event'leri
    this.mentionList.querySelectorAll('.mention-item').forEach(item => {
      item.addEventListener('click', () => {
        this.selectMention(item.getAttribute('data-user-id'), item.getAttribute('data-user-code'));
      });
    });
    
    // Stil ekle
    if (!document.querySelector('style#mention-list-styles')) {
      const style = document.createElement('style');
      style.id = 'mention-list-styles';
      style.textContent = `
        .mention-list {
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          background: var(--bg-primary);
          border: 1px solid var(--bg-tertiary);
          border-radius: var(--radius-md) var(--radius-md) 0 0;
          box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
          max-height: 200px;
          overflow-y: auto;
          z-index: 100;
          display: none;
        }
        
        .mention-item {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .mention-item:hover,
        .mention-item.selected {
          background-color: var(--bg-hover);
        }
        
        .mention-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
          background: var(--bg-tertiary);
        }
        
        .mention-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .avatar-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: var(--text-primary);
        }
        
        .mention-info {
          flex: 1;
          min-width: 0;
        }
        
        .mention-name {
          font-weight: 500;
          font-size: var(--font-sm);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .mention-role {
          font-size: var(--font-xs);
          padding: 2px 6px;
          background: var(--bg-accent-light);
          color: var(--bg-accent);
          border-radius: var(--radius-sm);
          font-weight: 600;
        }
        
        .mention-code {
          font-size: var(--font-xs);
          color: var(--text-secondary);
        }
        
        .message-mention {
          background: var(--bg-accent-light);
          color: var(--bg-accent);
          padding: 2px 4px;
          border-radius: var(--radius-sm);
          font-weight: 600;
        }
        
        .message-project {
          background: rgba(255, 193, 7, 0.18);
          color: #b38300;
          padding: 2px 6px;
          border-radius: 6px;
          font-weight: 600;
          text-decoration: none;
        }
        .message-project:hover {
          background: rgba(255, 193, 7, 0.28);
        }
        
        .message-group.sent .message-mention {
          background: rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.95);
        }
        .message-group.sent .message-project {
          background: rgba(255, 255, 255, 0.18);
          color: rgba(255, 255, 255, 0.95);
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  // Mention listesinde navigasyon
  navigateMentionList(direction) {
    if (!this.mentionList) return;
    
    const items = this.mentionList.querySelectorAll('.mention-item');
    const currentIndex = Array.from(items).findIndex(item => item.classList.contains('selected'));
    
    if (currentIndex === -1) return;
    
    // Seçimi kaldır
    items[currentIndex].classList.remove('selected');
    
    // Yeni index hesapla
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = items.length - 1;
    if (newIndex >= items.length) newIndex = 0;
    
    // Yeni seçimi yap
    items[newIndex].classList.add('selected');
    items[newIndex].scrollIntoView({ block: 'nearest' });
  }
  
  // Mention seçimi
  selectMention(userId, userCode) {
    const textarea = document.getElementById('message-textarea');
    if (!textarea || !this.currentMention) return;
    
    const text = textarea.value;
    const { atPosition } = this.currentMention;
    
    // @ dan önceki ve sonraki metinleri al
    const beforeAt = text.substring(0, atPosition);
    const afterCursor = text.substring(textarea.selectionStart);
    
    // Yeni metni oluştur
    const mentionText = `@${userCode} `;
    textarea.value = beforeAt + mentionText + afterCursor;
    
    // Cursor pozisyonunu ayarla
    const newCursorPos = beforeAt.length + mentionText.length;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    
    // Focus
    textarea.focus();
    
    // Listeyi kapat
    this.closeMentionList();
  }
  
  // Mention listesini kapat
  closeMentionList() {
    if (this.mentionList) {
      this.mentionList.style.display = 'none';
      this.currentMention = null;
    }
  }
  
  // Yanıt kullanıcı adını güncelle
  async updateReplyUserName(senderId, messageId) {
    try {
      const user = await userService.getUserProfile(senderId);
      if (user) {
        // Farklı alan isimlerini kontrol et
        const displayName = user.displayName || user.name || user.userCode || user.code || user.id?.substring(0, 6) || 'Kullanıcı';
        const role = user.userRole || user.role || '';
        
        // Cache'i güncelle (tutarlı format)
        this.userCache[senderId] = {
          displayName: displayName,
          role: role
        };
        
        // DOM'u güncelle
        const messageElement = this.messageListContainer.querySelector(`[data-id="${messageId}"]`);
        if (messageElement) {
          const repliedUserElement = messageElement.querySelector('.replied-user');
          if (repliedUserElement) {
            repliedUserElement.textContent = displayName;
          }
        }
      }
    } catch (error) {
      console.warn('Yanıt kullanıcı adı alınamadı:', senderId);
    }
  }
  
  // Etiketlenebilir kullanıcıları güncelle
  async updateMentionUsers() {
    if (!this.currentChatId) return;
    
    try {
      const chatDetails = await chatService.getChatDetails(this.currentChatId);
      const participantIds = chatDetails.participants || chatDetails.users || [];
      
      // Kendi kullanıcımızı hariç tut
      const otherUserIds = participantIds.filter(uid => uid !== this.currentUser?.uid);
      
      // Kullanıcı detaylarını al
      this.mentionUsers = [];
      for (const userId of otherUserIds) {
        try {
          // Önce cache'den kontrol et
          let user = this.userCache[userId];
          
          // Cache'de yoksa Firestore'dan çek
          if (!user) {
            user = await userService.getUserProfile(userId);
            this.userCache[userId] = user;
          }
          
          if (user) {
            this.mentionUsers.push({
              uid: userId,
              userCode: user.userCode,
              displayName: user.displayName || user.userCode,
              photoURL: user.photoURL || '',
              role: user.role || 'register'
            });
          }
        } catch (error) {
          console.warn(`Kullanıcı bilgisi alınamadı: ${userId}`, error);
        }
      }
      
    } catch (error) {
      console.warn('Etiketlenebilir kullanıcılar alınamadı:', error);
    }
  }
}

export async function initChatWindow(user) {
  const headerContainer = document.getElementById('chat-header');
  const messageListContainer = document.getElementById('message-list');
  const messageInputContainer = document.getElementById('message-input');
  
  if (headerContainer && messageListContainer && messageInputContainer) {
    const chatWindow = new ChatWindow(headerContainer, messageListContainer, messageInputContainer);
    if (user) {
      await chatWindow.setUser(user);
    }
    return chatWindow;
  }
  
  return null;
} 